<template>
  <div class="barcode-cost-page" @click="keepBarcodeFocus">
    <div class="barcode-cost-header sticky-top text-white p-3">
      <b-row align-v="center">
        <b-col cols="2">
          <b-button variant="link" @click="goBack()" class="text-white p-0">
            <AppIcon name="back" font-scale="1.5" />
          </b-button>
        </b-col>
        <b-col cols="8" class="text-center">
          <h5 class="mb-0">Gestión de producto</h5>
          <small class="barcode-cost-header__subtitle">{{ product ? product.name : 'Gestión centralizada del producto por código de barra' }}</small>
        </b-col>
        <b-col cols="2" class="text-right">
          <b-button
            variant="link"
            @click="toggleFocusMode()"
            class="text-white p-0"
            :title="focusMode ? 'Salir de pantalla completa' : 'Usar pantalla completa'"
          >
            <AppIcon name="arrows-fullscreen" font-scale="1.2" />
          </b-button>
        </b-col>
      </b-row>
    </div>

    <div class="barcode-cost-body p-3 pb-5">
      <b-card v-if="!formReady" class="mb-3 shadow-sm search-card">
        <div style="position:relative">
          <b-input-group size="lg">
            <!-- Botón toggle de modo (izquierda) -->
            <b-input-group-prepend>
              <b-button
                class="search-mode-btn"
                :variant="searchMode === 'barcode' ? 'primary' : 'info'"
                :title="searchMode === 'barcode' ? 'Cambiar a búsqueda manual (nombre, código, categoría)' : 'Cambiar a escaneo por código de barras'"
                @click="switchSearchMode(searchMode === 'barcode' ? 'manual' : 'barcode')"
              >
                <AppIcon :name="searchMode === 'barcode' ? 'upc-scan' : 'search'" />
              </b-button>
            </b-input-group-prepend>

            <!-- Input modo barcode -->
            <b-form-input
              v-if="searchMode === 'barcode'"
              ref="barcodeInput"
              v-model="barcode"
              type="text"
              placeholder="Esperando lector..."
              :disabled="loading.search"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              :inputmode="isMobileLayout ? 'text' : 'none'"
            />
            <!-- Input modo manual -->
            <b-form-input
              v-else
              ref="manualSearchInput"
              v-model="manualSearch.query"
              placeholder="Nombre, código, categoría o subcategoría..."
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              :disabled="loading.search"
              @keydown.down.prevent="manualSearchMoveDown"
              @keydown.up.prevent="manualSearchMoveUp"
              @keydown.enter.prevent="manualSearchConfirm"
              @blur="manualSearchBlur"
              @focus="manualSearchFocus"
            />

            <!-- Botones derecha: modo barcode -->
            <b-input-group-append v-if="searchMode === 'barcode'">
              <b-button variant="outline-secondary" :disabled="loading.search" title="Escanear con cámara" @click="startCamera()">
                <AppIcon v-if="!loading.search" name="camera" />
                <b-spinner v-else small></b-spinner>
              </b-button>
            </b-input-group-append>
            <!-- Spinner derecha: modo manual cargando -->
            <b-input-group-append v-else-if="manualSearch.loading || loading.search">
              <b-input-group-text><b-spinner small></b-spinner></b-input-group-text>
            </b-input-group-append>
          </b-input-group>

          <!-- Hint contextual -->
          <small class="search-mode-hint text-muted">
            <template v-if="searchMode === 'barcode'">
              Modo escáner — el código se captura automáticamente · clic en
              <AppIcon name="upc-scan" /> para cambiar a búsqueda manual
            </template>
            <template v-else>
              Presioná <kbd>Enter</kbd> para buscar · <kbd>↑↓</kbd> navegar resultados · clic en
              <AppIcon name="search" /> para volver al escáner
            </template>
          </small>

          <!-- Resultados búsqueda manual -->
          <div v-if="searchMode === 'manual' && manualSearch.show" ref="manualDropdown" class="manual-search-results">
            <div v-if="manualSearch.loading" class="manual-search-empty text-muted">
              <b-spinner small class="mr-1"></b-spinner> Buscando...
            </div>
            <template v-else>
              <div
                v-for="(item, idx) in manualSearch.results"
                :key="item.id"
                class="manual-search-item"
                :class="{ 'manual-search-item--active': idx === manualSearch.activeIdx }"
                @mousedown.prevent="selectManualResult(item)"
                @touchstart="onDropdownItemTouchStart"
                @touchend.prevent="onDropdownItemTouchEnd(item, $event)"
              >
                <div class="manual-search-item__body">
                  <div class="manual-search-item__name">{{ item.name }}</div>
                  <div class="manual-search-item__meta">
                    <code v-if="item.code" class="manual-search-item__code">{{ item.code }}</code>
                    <code v-if="item.barcode && item.barcode !== item.code" class="manual-search-item__barcode">
                      <AppIcon name="upc-scan" class="mr-1" />{{ item.barcode }}
                    </code>
                    <span v-if="item.category">{{ item.category }}</span>
                    <span v-if="item.subcategory"> / {{ item.subcategory }}</span>
                  </div>
                </div>
                <AppIcon name="chevron-right" class="text-muted flex-shrink-0" />
              </div>

              <div v-if="!manualSearch.results.length" class="manual-search-empty text-muted">
                Sin resultados para "<strong>{{ manualSearch.query }}</strong>"
              </div>

              <!-- Opción: crear producto nuevo -->
              <div
                v-if="manualSearch.query.trim()"
                class="manual-search-item manual-search-item--new"
                :class="{ 'manual-search-item--active': manualSearch.activeIdx === manualSearch.results.length }"
                @mousedown.prevent="selectNewProductWithoutBarcode()"
                @touchstart="onDropdownItemTouchStart"
                @touchend.prevent="onDropdownItemTouchEnd(null, $event)"
              >
                <div class="manual-search-item__body">
                  <div class="manual-search-item__name text-success">
                    <AppIcon name="plus-circle" class="mr-1" />
                    Crear producto "{{ manualSearch.query }}"
                  </div>
                  <div class="manual-search-item__meta text-muted">
                    <small>Sin código de barras — se puede agregar después</small>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <b-alert v-if="searchError" variant="danger" show class="mt-2 mb-0 py-2">
          <AppIcon name="exclamation-circle" class="mr-1" />{{ searchError }}
        </b-alert>
      </b-card>

      <template v-if="!formReady">
        <div class="product-sections-toggle mb-3">
          <b-form-checkbox v-model="sections.detail" switch size="sm" class="mb-0">
            <small>Detalle</small>
          </b-form-checkbox>
          <b-form-checkbox v-model="sections.suppliers" switch size="sm" class="mb-0">
            <small>Proveedores</small>
          </b-form-checkbox>
          <b-form-checkbox v-model="sections.priceLists" switch size="sm" class="mb-0">
            <small>Precios</small>
          </b-form-checkbox>
          <b-form-checkbox v-model="sections.stock" switch size="sm" class="mb-0">
            <small>Stock</small>
          </b-form-checkbox>
        </div>
        <EmptyState
          icon="box-seam"
          title="Aún no se cargó ningún producto"
          :description="searchMode === 'barcode' ? 'Escaneá o ingresá un código de barras para comenzar.' : 'Escribí el nombre, código o categoría y presioná Enter.'"
          class="py-4" />
      </template>

      <div v-if="formReady">
        <b-card class="mb-3 shadow-sm product-header-card" :class="createdInSession ? (creationStatus === 'complete' ? 'border-success' : creationStatus === 'warning' ? 'border-warning' : 'border-danger') : (productExists ? 'border-primary' : 'border-warning')">
          <div class="d-flex align-items-center justify-content-between">
            <div class="flex-grow-1 mr-3">
              <div class="d-flex align-items-center mb-1">
                <b-badge :variant="productExists ? 'primary' : 'warning'" pill class="product-header-badge mr-2">
                  {{ productExists ? 'Existente' : 'Nuevo' }}
                </b-badge>
                <code class="product-header-barcode">{{ form.barcode || '(sin código de barras)' }}</code>
              </div>
              <h5 class="mb-0 font-weight-bold product-header-name">{{ form.name || 'Producto nuevo' }}</h5>
            </div>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="product-header-reset flex-shrink-0"
              @click="resetSearch()"
              title="Buscar otro producto"
            >
              <AppIcon name="upc-scan" :class="isMobileLayout ? '' : 'mr-1'" />
              <span class="d-none d-sm-inline">Buscar otro</span>
            </b-button>
          </div>
          <div v-if="createdInSession" class="product-progress mt-3" :class="'product-progress--' + creationStatus">
            <button type="button" class="product-progress__toggle" :aria-expanded="guideExpanded ? 'true' : 'false'" @click="guideExpanded = !guideExpanded">
              <span class="product-progress__indicator">
                <AppIcon :name="creationStatus === 'complete' ? 'check2' : 'exclamation-circle'" />
              </span>
              <span class="product-progress__message">
                <strong>{{ creationStatus === 'complete' ? 'Producto configurado' : 'Próximo paso' }}</strong>
                <span :key="nextCreationStep ? nextCreationStep.label : 'complete'" class="product-progress__next">
                  {{ nextCreationStep ? nextCreationStep.label : 'Listo para vender' }}
                </span>
              </span>
              <span class="product-progress__count">{{ completedRequiredCreationSteps }}/{{ requiredCreationSteps.length }}</span>
              <AppIcon :name="guideExpanded ? 'chevron-up' : 'chevron-down'" class="product-progress__chevron" />
            </button>
            <div v-if="guideExpanded" class="product-progress__details">
              <div v-for="step in creationSteps" :key="step.label" class="product-progress__step" :class="{ 'product-progress__step--done': step.done }">
                <AppIcon v-if="step.done || step.required" :name="step.done ? 'check2' : 'x'" class="product-progress__step-icon" />
                <span v-else class="product-progress__step-icon">–</span>
                <span>{{ step.label }}<small v-if="!step.required"> (opcional)</small></span>
              </div>
              <small v-if="!productExists" class="product-progress__hint">El producto se guarda al agregar el primer proveedor.</small>
            </div>
          </div>
        </b-card>
        <div class="product-sections-toggle mb-3">
          <b-form-checkbox v-model="sections.detail" switch size="sm" class="mb-0">
            <small>Detalle</small>
          </b-form-checkbox>
          <b-form-checkbox v-model="sections.suppliers" switch size="sm" class="mb-0">
            <small>Proveedores</small>
          </b-form-checkbox>
          <b-form-checkbox v-model="sections.priceLists" switch size="sm" class="mb-0">
            <small>Precios</small>
          </b-form-checkbox>
          <b-form-checkbox v-model="sections.stock" switch size="sm" class="mb-0">
            <small>Stock</small>
          </b-form-checkbox>
        </div>

        <b-row>
          <!-- Columna izquierda: Datos del producto (con unidades e IVA integrados) -->
          <b-col cols="12" xl="6" v-show="sections.detail">
            <b-card class="mb-3 shadow-sm position-relative">
              <div v-if="loading.options" class="card-loading-overlay">
                <b-spinner small></b-spinner>
                <span class="ml-2 small text-muted">Cargando opciones...</span>
              </div>
              <div class="card-section-heading mb-3">
                <h6 class="section-title mb-0">
                  <AppIcon name="box-seam" class="section-title__icon" />
                  Detalle del producto
                </h6>
                <b-badge v-if="productDetailDirty" variant="warning" pill>
                  <AppIcon name="pencil-square" class="mr-1" />Cambios sin guardar
                </b-badge>
              </div>

              <field-group
                label="Código de barras"
                label-icon="upc"
                label-hint="habilitar para modificar"
                field="barcode"
                @reset="resetField"
              >
                <b-form-input
                  ref="productBarcodeInput"
                  v-model="form.barcode"
                  placeholder="Escanear o ingresar código"
                  autocomplete="off"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                  :readonly="!barcodeEditEnabled"
                  :class="{ 'barcode-edit-readonly': !barcodeEditEnabled }"
                ></b-form-input>
                <b-input-group-append>
                  <b-button
                    :variant="barcodeEditEnabled ? 'primary' : 'outline-primary'"
                    :title="barcodeEditEnabled ? 'Código habilitado para edición' : 'Modificar código de barras'"
                    @click="enableBarcodeEdit"
                  >
                    <AppIcon :name="barcodeEditEnabled ? 'unlock' : 'pencil-square'" />
                  </b-button>
                </b-input-group-append>
              </field-group>

              <template v-if="showProductCodeFields">
                <div class="section-divider">Códigos</div>
                <b-row>
                  <b-col cols="12" sm="6">
                    <field-group label="Código de producto" label-hint="cuando difiere del código de barras" field="code" @reset="resetField">
                      <b-form-input
                        ref="productCodeInput"
                        v-model="form.code"
                        placeholder="Ej: PROV-001"
                        :readonly="!codeEditEnabled.code"
                        :class="{ 'barcode-edit-readonly': !codeEditEnabled.code }"
                      ></b-form-input>
                      <b-input-group-append>
                        <b-button
                          :variant="codeEditEnabled.code ? 'primary' : 'outline-primary'"
                          :title="codeEditEnabled.code ? 'Código habilitado para edición' : 'Modificar código de producto'"
                          @click="enableCodeEdit('code')"
                        >
                          <AppIcon :name="codeEditEnabled.code ? 'unlock' : 'pencil-square'" />
                        </b-button>
                      </b-input-group-append>
                    </field-group>
                  </b-col>                  
                  <b-col cols="12" sm="6">
                    <field-group label="Código propio" label-hint="identificador interno" field="code_system" @reset="resetField">
                      <b-form-input
                        ref="productCodeSystemInput"
                        v-model="form.code_system"
                        placeholder="Ej: REF-001"
                        :readonly="!codeEditEnabled.code_system"
                        :class="{ 'barcode-edit-readonly': !codeEditEnabled.code_system }"
                      ></b-form-input>
                      <b-input-group-append>
                        <b-button
                          :variant="codeEditEnabled.code_system ? 'primary' : 'outline-primary'"
                          :title="codeEditEnabled.code_system ? 'Código habilitado para edición' : 'Modificar código propio'"
                          @click="enableCodeEdit('code_system')"
                        >
                          <AppIcon :name="codeEditEnabled.code_system ? 'unlock' : 'pencil-square'" />
                        </b-button>
                      </b-input-group-append>
                    </field-group>
                  </b-col>
                </b-row>
              </template>

              <field-group label="Nombre" field="name" @reset="resetField">
                <b-form-input v-model="form.name" placeholder="Nombre del producto"></b-form-input>
              </field-group>
              <field-group v-if="parameters.enableProductShortName" label="Nombre corto" field="name_short" @reset="resetField">
                <b-form-input v-model="form.name_short" placeholder="Nombre corto"></b-form-input>
              </field-group>

              <div class="section-divider">Clasificación</div>

              <b-row>
                <b-col cols="12" lg="6">
                  <field-group label="Categoría" field="category" @reset="resetField">
                    <smart-select
                      :options="categoryOptions"
                      :value="findOption(categoryOptions, form.category)"
                      :clearable="false"
                      @input="selectOption('category', $event, onCategoryChange)"
                    ></smart-select>
                  </field-group>
                </b-col>
                <b-col cols="12" lg="6">
                  <field-group label="Subcategoría" field="subcategory" @reset="resetField">
                    <smart-select
                      :options="subcategoryOptions"
                      :value="findOption(subcategoryOptions, form.subcategory)"
                      :clearable="false"
                      @input="selectOption('subcategory', $event, onSubcategoryChange)"
                    ></smart-select>
                  </field-group>
                </b-col>
              </b-row>

              <div class="section-divider">Unidades de medida</div>

              <b-row>
                <b-col cols="12" sm="6">
                  <field-group
                    label="Compra"
                    label-icon="box-arrow-in-down"
                    label-hint="unidad en que ingresa"
                    field="unit_measurement_purchase_id"
                    @reset="resetField"
                  >
                    <smart-select
                      :options="unitOptions"
                      :value="findOption(unitOptions, form.unit_measurement_purchase_id)"
                      :clearable="false"
                      @input="selectOption('unit_measurement_purchase_id', $event)"
                    ></smart-select>
                  </field-group>
                </b-col>
                <b-col cols="12" sm="6">
                  <field-group
                    label="Venta"
                    label-icon="bag"
                    label-hint="unidad con que se vende"
                    field="unit_measurement_sale_id"
                    @reset="resetField"
                  >
                    <smart-select
                      :options="unitOptions"
                      :value="findOption(unitOptions, form.unit_measurement_sale_id)"
                      :clearable="false"
                      @input="selectOption('unit_measurement_sale_id', $event)"
                    ></smart-select>
                  </field-group>
                </b-col>
                <b-col v-if="form.unit_measurement_purchase_id !== form.unit_measurement_sale_id" cols="12" class="mt-2 mb-3">
                  <label class="unit-conversion-label">
                    <AppIcon name="arrow-left-right" class="mr-1" />Factor de conversión
                  </label>
                  <b-input-group>
                    <formatted-number-input v-model="form.unit_measurement_sale_conversion"></formatted-number-input>
                    <b-input-group-append>
                      <b-button variant="outline-secondary" title="Restablecer" @click="resetField('unit_measurement_sale_conversion')">
                        <AppIcon name="arrow-counterclockwise" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-muted">1 unidad de compra = X unidades de venta</small>
                </b-col>
                <b-col cols="12" class="mb-3">
                  <b-form-checkbox v-model="form.weighable" switch>Producto pesable</b-form-checkbox>
                  <small class="text-muted">Se pesa al preparar el pedido. Requiere conversión entre la unidad de venta y KG.</small>
                </b-col>
              </b-row>

              <div class="section-divider">Datos fiscales</div>

              <b-row>
                <b-col cols="12" lg="6">
                  <field-group label="Condición fiscal" field="iva_condition_id" @reset="resetField">
                    <smart-select
                      :options="ivaOptions"
                      :value="findOption(ivaOptions, form.iva_condition_id)"
                      :clearable="false"
                      @input="selectOption('iva_condition_id', $event)"
                    ></smart-select>
                  </field-group>
                </b-col>
              </b-row>

              <b-button v-if="productExists && productDetailDirty" block variant="success" :disabled="loading.save" class="mt-3 d-none d-md-block" @click="saveProduct()">
                <b-spinner small v-if="loading.save" class="mr-1"></b-spinner>
                <AppIcon name="check2-circle" v-else class="mr-1" />
                Guardar producto
              </b-button>
            </b-card>
          </b-col>

          <!-- Columna derecha: Proveedores + Listas de precios -->
          <b-col cols="12" xl="6">
            <b-card v-show="sections.suppliers" class="mb-3 shadow-sm mobile-collapsible-card" @click="clearSupplierSelection()">
              <div class="card-section-heading mb-3">
                <h6 class="section-title mb-0">
                  <AppIcon name="truck" class="section-title__icon" />
                  Proveedores que lo venden
                  <b-badge variant="light" pill class="section-title__count">
                    {{ suppliersProducts.length }}
                  </b-badge>
                </h6>
                <div class="d-flex align-items-center" style="gap:0.5rem;">
                  <b-spinner small v-if="loading.options"></b-spinner>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mobile-panel-toggle"
                    :title="mobilePanels.suppliers ? 'Minimizar proveedores' : 'Maximizar proveedores'"
                    @click.stop="toggleMobilePanel('suppliers')"
                  >
                    <AppIcon :name="mobilePanels.suppliers ? 'chevron-up' : 'chevron-down'" />
                  </b-button>
                </div>
              </div>

              <div v-if="showSuppliersPanel">
                <div v-if="suppliersProducts.length" class="supplier-list mb-3">
                  <small class="supplier-list__hint text-muted d-block mb-1">
                    <AppIcon name="hand-index" /> Tocá o hacé clic para seleccionar
                  </small>
                  <div
                    v-for="item in suppliersProducts"
                    :key="item.suppliers_id"
                    class="supplier-item"
                    :class="{
                      'supplier-item--selected': item.id === selectedSupplierProductId,
                      'supplier-item--default': item.default
                    }"
                    @click.stop="selectSupplierProduct(item)"
                  >
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="supplier-item__body">
                        <div class="d-flex align-items-center flex-wrap">
                          <span class="supplier-item__name mr-2">{{ item.supplier_name }}</span>
                          <b-badge v-if="item.default" variant="warning" pill class="supplier-item__badge">Default</b-badge>
                          <b-badge v-if="item.pending" variant="light" pill class="ml-1 border supplier-item__badge">Nuevo</b-badge>
                        </div>
                        <div class="supplier-item__details">
                          <span v-if="item.code" class="supplier-item__code">{{ item.code }}</span>
                          <template v-if="item.price_cost !== null && item.price_cost !== undefined">
                            <span class="supplier-item__cost supplier-item__cost--iva-primary">{{ money(costWithIva(item.price_cost)) }} <small>c/IVA</small></span>
                            <span class="supplier-item__cost supplier-item__cost--iva-secondary">{{ money(item.price_cost) }} <small>s/IVA</small></span>
                          </template>
                        </div>
                        <div class="supplier-item__meta">
                          <span class="supplier-item__meta-item" :class="{ 'supplier-item__meta-item--empty': !item.last_purchase }">
                            <AppIcon name="basket" class="mr-1" />
                            <span class="supplier-item__meta-label">Compra:</span>
                            <span :class="dateAgoClass(item.last_purchase)" :title="formatDate(item.last_purchase)">
                              {{ item.last_purchase ? daysAgo(item.last_purchase) : 'Sin compras registradas' }}
                            </span>
                          </span>
                          <span class="supplier-item__meta-item" :class="{ 'supplier-item__meta-item--empty': !item.last_update_price }">
                            <AppIcon name="refresh" class="mr-1" />
                            <span class="supplier-item__meta-label">Precio:</span>
                            <span :class="dateAgoClass(item.last_update_price)" :title="formatDate(item.last_update_price)">
                              {{ item.last_update_price ? daysAgo(item.last_update_price) : 'Sin actualizaciones' }}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div class="d-flex align-items-center flex-shrink-0 ml-2" style="gap:0.35rem">
                        <AppIcon v-if="item.default" name="star-fill" class="text-warning" font-scale="1.1" title="Proveedor default" />
                        <b-button
                          v-if="productExists && form.products_id"
                          size="sm"
                          variant="outline-primary"
                          class="supplier-oc-btn"
                          title="Agregar a orden de compra"
                          @click.stop="openQuickAddOrder(item)"
                        >
                          <AppIcon name="cart-plus" />
                        </b-button>
                      </div>
                    </div>
                    <div v-if="item.id === selectedSupplierProductId" class="supplier-item__action-bar">
                      <b-button
                        size="sm"
                        variant="primary"
                        @click.stop="openPriceModal(item)"
                      >
                        <AppIcon name="pencil-square" class="mr-1" />Actualizar precio
                      </b-button>
                      <b-button
                        v-if="!item.default"
                        size="sm"
                        variant="outline-warning"
                        :disabled="loading.default"
                        @click.stop="setDefault(item)"
                      >
                        <AppIcon name="star" class="mr-1" />Marcar default
                      </b-button>
                      <b-button
                        v-if="suppliersProducts.length > 1"
                        size="sm"
                        variant="outline-danger"
                        :disabled="loading.remove"
                        @click.stop="removeSupplier(item)"
                      >
                        <AppIcon name="trash" class="mr-1" />Quitar
                      </b-button>
                    </div>
                  </div>
                </div>
                <b-alert v-else variant="info" show class="py-2">
                  <AppIcon name="info-circle" />
                  <small class="ml-1">Este producto todavía no tiene proveedores vinculados.</small>
                </b-alert>

                <div class="add-supplier-box">
                  <label class="supplier-add-label">
                    <AppIcon name="plus-circle" class="mr-1" />Agregar proveedor
                  </label>
                  <b-input-group class="supplier-add-group">
                    <smart-select
                      class="supplier-add-select"
                      :options="availableSupplierOptions"
                      :value="findOption(availableSupplierOptions, newSupplierId)"
                      :clearable="false"
                      :disabled="loading.options"
                      @input="selectNewSupplier"
                      @blur="onNewSupplierBlur"
                    ></smart-select>
                    <b-input-group-append>
                      <b-button variant="primary" class="supplier-add-button" :disabled="!newSupplierId || loading.addSupplier || supplierConfirmPending" @click.stop="addSupplierToProduct()">
                        <b-spinner small v-if="loading.addSupplier"></b-spinner>
                        <AppIcon name="plus" v-else />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
              </div>
            </b-card>

            <b-card v-show="sections.priceLists" class="mb-3 shadow-sm position-relative mobile-collapsible-card">
              <div v-if="loading.options" class="card-loading-overlay">
                <b-spinner small></b-spinner>
                <span class="ml-2 small text-muted">Cargando listas de precios...</span>
              </div>
              <div class="card-section-heading mb-3">
                <h6 class="section-title mb-0">
                  <AppIcon name="tags" class="section-title__icon" />
                  Precio de Venta
                </h6>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mobile-panel-toggle"
                  :title="mobilePanels.priceLists ? 'Minimizar listas de precios' : 'Maximizar listas de precios'"
                  @click="toggleMobilePanel('priceLists')"
                >
                  <AppIcon :name="mobilePanels.priceLists ? 'chevron-up' : 'chevron-down'" />
                </b-button>
              </div>

              <div v-if="showPriceListsPanel">
                <b-alert v-if="!parameters.highPrecisionPricing" variant="warning" show class="py-2 mb-3 small">
                  <AppIcon name="info-circle" class="mr-1" />El precio final puede variar unos centavos al guardar. Para evitarlo, activá el parámetro del sistema “Conservar precisión en precios de venta” en el módulo Costos.
                </b-alert>
                <b-alert v-if="priceListsReadOnly" variant="warning" show class="py-2 mb-3">
                  <AppIcon name="exclamation-triangle" class="mr-1" />
                  Guardá el cambio de IVA del producto antes de editar las listas de precios.
                </b-alert>

                <div v-if="!defaultSupplierProduct" class="text-muted small mb-3 py-2 text-center">
                  <AppIcon name="info-circle" class="mr-1" />Asigná un proveedor default para calcular los precios de venta
                </div>

                <div
                  v-for="list in form.lists"
                  :key="list.price_list_id"
                  class="price-list-block"
                  :class="{
                    'price-list-block--active': list.add,
                    'price-list-block--inactive': !list.add,
                    'price-list-block--readonly': priceListsReadOnly
                  }"
                >
                <div class="price-list-block__header">
                  <b-form-checkbox v-model="list.add" switch class="price-list-block__toggle" :disabled="priceListsReadOnly" @change="onListToggle(list)">
                    <span class="price-list-block__name">{{ list.name }}</span>
                  </b-form-checkbox>
                  <div class="d-flex align-items-center" style="gap:0.4rem;">
                    <b-spinner v-if="list.saving" small variant="secondary" style="width:0.75rem;height:0.75rem;"></b-spinner>
                    <AppIcon v-else-if="list.saved" name="check2" class="text-success" />
                    <span class="price-list-block__result" :class="defaultSupplierProduct ? 'price-list-block__result--active' : 'text-muted'">
                      {{ defaultSupplierProduct ? money(ivaAliquot > 0 ? list.sale_price_iva : list.sale_price) : '—' }}
                    </span>
                  </div>
                </div>
                <div v-if="list.saving || list.saved" class="price-list-save-status" :class="list.saved ? 'price-list-save-status--saved' : ''">
                  <b-spinner v-if="list.saving" small class="mr-1"></b-spinner>
                  <AppIcon v-else name="check2-circle" class="mr-1" />
                  {{ list.saving ? 'Guardando cambios...' : 'Guardado en la base de datos' }}
                </div>

                <template v-if="list.add">
                <b-button
                  variant="link"
                  size="sm"
                  class="price-list-calc-toggle"
                  @click="list.show_calculation = !list.show_calculation"
                >
                  <AppIcon :name="list.show_calculation ? 'chevron-up' : 'calculator'" class="mr-1" />
                  {{ list.show_calculation ? 'Ocultar cálculo' : 'Ver cálculo' }}
                </b-button>

                <!-- Desglose paso a paso -->
                <div v-if="list.show_calculation" class="plc-box">
                  <div class="plc-row">
                    <span class="plc-label text-muted">Costo base</span>
                    <span class="plc-bar"></span>
                    <span class="plc-val">{{ money(baseCost) }}</span>
                  </div>
                  <div class="plc-row plc-row--add">
                    <span class="plc-label">
                      + {{ effectiveListUtility(list) }}% utilidad
                      <span v-if="list.using_default_utility" class="plc-hint">(default lista)</span>
                    </span>
                    <span class="plc-bar"></span>
                    <span class="plc-val">{{ money(roundMoney(baseCost * (1 + effectiveListUtility(list) / 100))) }}</span>
                  </div>
                  <div v-if="toNumber(list.desc) > 0" class="plc-row plc-row--sub">
                    <span class="plc-label">− {{ toNumber(list.desc) }}% descuento</span>
                    <span class="plc-bar"></span>
                    <span class="plc-val">{{ money(list.sale_price) }}</span>
                  </div>
                  <div class="plc-row plc-row--siva">
                    <span class="plc-label font-weight-bold">Precio s/IVA</span>
                    <span class="plc-bar"></span>
                    <span class="plc-val font-weight-bold">{{ money(list.sale_price) }}</span>
                  </div>
                  <div v-if="ivaAliquot > 0" class="plc-row plc-row--iva">
                    <span class="plc-label">+ {{ ivaAliquot }}% IVA</span>
                    <span class="plc-bar"></span>
                    <span class="plc-val font-weight-bold" style="color:#1d4ed8;">{{ money(list.sale_price_iva) }}</span>
                  </div>
                </div>

                <!-- Inputs utilidad y descuento -->
                <b-row class="mt-2">
                  <b-col cols="6">
                    <b-form-group label="Utilidad %" label-size="sm" class="mb-1">
                      <b-input-group size="sm">
                        <formatted-number-input
                          v-model="list.utility"
                          :disabled="priceListsReadOnly"
                          @input="updateSalePrice(list)"
                          @blur="saveListField(list)"
                        ></formatted-number-input>
                        <b-input-group-append is-text><small class="text-muted">%</small></b-input-group-append>
                        <b-input-group-append v-if="listHasPersistedChanges(list)">
                          <b-button
                            variant="outline-success"
                            size="sm"
                            class="price-list-save-btn"
                            :disabled="list.saving"
                            :title="'Guardar utilidad de ' + list.name"
                            @mousedown.prevent="prepareManualListSave(list)"
                            @click="forceSaveListField(list)"
                          >
                            <AppIcon name="check2" />
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                      <small v-if="list.using_default_utility" class="text-muted" style="font-size:0.68rem;">Default lista: {{ list.default_utility }}%</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group label="Descuento %" label-size="sm" class="mb-1">
                      <b-input-group size="sm">
                        <formatted-number-input
                          v-model="list.desc"
                          :disabled="priceListsReadOnly"
                          @input="updateSalePrice(list)"
                          @blur="saveListField(list)"
                        ></formatted-number-input>
                        <b-input-group-append is-text><small class="text-muted">%</small></b-input-group-append>
                        <b-input-group-append v-if="listHasPersistedChanges(list)">
                          <b-button
                            variant="outline-success"
                            size="sm"
                            class="price-list-save-btn"
                            :disabled="list.saving"
                            :title="'Guardar descuento de ' + list.name"
                            @mousedown.prevent="prepareManualListSave(list)"
                            @click="forceSaveListField(list)"
                          >
                            <AppIcon name="check2" />
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Input precio de venta destacado -->
                <div class="price-list-sale-row">
                  <div class="price-list-sale-row__label">
                    <AppIcon name="tag" class="mr-1" />
                    Fijar precio de venta {{ ivaAliquot > 0 ? 'c/IVA' : '' }}
                    <small class="text-muted ml-1">→ calcula la utilidad automáticamente</small>
                  </div>
                  <b-input-group>
                    <b-input-group-prepend is-text><span class="font-weight-bold text-primary">$</span></b-input-group-prepend>
                    <formatted-number-input
                      v-model="list.sale_price_iva"
                      input-class="price-list-sale-row__input"
                      :disabled="priceListsReadOnly"
                      @input="updateUtilityFromSalePrice(list)"
                      @blur="saveListField(list)"
                    ></formatted-number-input>
                    <b-input-group-append v-if="listHasPersistedChanges(list)">
                      <b-button
                        variant="outline-success"
                        size="sm"
                        class="price-list-save-btn"
                        :disabled="list.saving"
                        :title="'Guardar precio de venta de ' + list.name"
                        @mousedown.prevent="prepareManualListSave(list)"
                        @click="forceSaveListField(list)"
                      >
                        <AppIcon name="check2" class="mr-1" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
                </template>
              </div>
              </div>
            </b-card>

            <!-- Stock -->
            <b-card v-show="sections.stock" class="mb-3 shadow-sm mobile-collapsible-card">
              <div class="card-section-heading mb-3">
                <h6 class="section-title mb-0">
                  <AppIcon name="box-seam" class="section-title__icon" />
                  Stock actual
                  <b-spinner small v-if="stock.loading" variant="secondary" class="ml-2"></b-spinner>
                </h6>
                <div class="d-flex align-items-center" style="gap:0.4rem;">
                  <b-button
                    v-if="productExists && productControlsStock && stock.byDepositRaw.length"
                    variant="outline-success"
                    size="sm"
                    :disabled="stock.loading"
                    title="Ajustar stock por depósito"
                    @click.stop="openStockAdjustModal()"
                  >
                    <AppIcon name="pencil-square" class="mr-1" />Ajustar
                  </b-button>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mobile-panel-toggle"
                    :title="mobilePanels.stock ? 'Minimizar stock' : 'Maximizar stock'"
                    @click.stop="toggleMobilePanel('stock')"
                  >
                    <AppIcon :name="mobilePanels.stock ? 'chevron-up' : 'chevron-down'" />
                  </b-button>
                </div>
              </div>

              <div v-if="showStockPanel">
                <b-alert v-if="productExists && !productControlsStock" variant="warning" show class="py-2 mb-0">
                  <AppIcon name="info-circle" class="mr-1" />
                  <small>Este producto no controla stock. No se muestran existencias ni se permiten ajustes por depósito.</small>
                </b-alert>

                <div v-else-if="stock.loading" class="text-center text-muted py-3">
                  <b-spinner small class="mr-2"></b-spinner>
                  <small>Cargando stock...</small>
                </div>

                <div v-else>
                  <!-- Total prominente -->
                  <div class="stock-total-hero">
                    <div class="stock-total-hero__number">{{ formatStockQty(stockTotal) }}</div>
                    <div class="stock-total-hero__unit" v-if="selectedStockUnit">
                      {{ selectedStockUnit.reference || selectedStockUnit.name || '' }}
                    </div>
                  </div>

                  <!-- Toggle de unidad cuando compra ≠ venta -->
                  <div v-if="stock.unitMeasurements.length > 1" class="stock-unit-toggle mt-3">
                    <button
                      v-for="u in stock.unitMeasurements"
                      :key="u.id"
                      class="stock-unit-btn"
                      :class="{ 'stock-unit-btn--active': stock.selectedUnitId == u.id }"
                      @click="stock.selectedUnitId = u.id; applyStockUnit()"
                    >
                      <span class="stock-unit-btn__ref">{{ u.reference || u.name }}</span>
                      <span class="stock-unit-btn__type">{{ u.type === 'sale' ? 'venta' : 'compra' }}</span>
                    </button>
                  </div>

                  <!-- Desglose por depósito -->
                  <div v-if="stock.byDeposit.length" class="stock-deposits-list mt-3">
                    <div
                      v-for="dep in stock.byDeposit"
                      :key="dep.deposit.id"
                      class="stock-deposit-row"
                    >
                      <span class="stock-deposit-name">
                        <AppIcon name="building" class="mr-1 text-muted" />
                        {{ dep.deposit.name }}
                      </span>
                      <span class="stock-deposit-qty" :class="toNumber(dep.current_quantity) <= 0 ? 'stock-deposit-qty--zero' : ''">
                        {{ formatStockQty(dep.current_quantity) }}
                      </span>
                    </div>
                  </div>
                  <b-alert v-else variant="info" show class="py-2 mb-0 mt-3">
                    <AppIcon name="info-circle" class="mr-1" />
                    <small>Sin depósitos configurados</small>
                  </b-alert>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

      </div>
    </div>

    <div v-if="formReady && productExists && productDetailDirty" class="fixed-bottom barcode-cost-footer border-top p-3 d-md-none">
      <b-button block variant="success" size="lg" :disabled="loading.save" @click="saveProduct()">
        <b-spinner small v-if="loading.save" class="mr-1" />
        <AppIcon v-else name="save" class="mr-1" />
        Guardar producto
      </b-button>
    </div>

    <b-modal
      v-model="modal.supplierPrice"
      centered
      size="lg"
      button-size="lg"
      :ok-title="form.type === 'EDITAR' ? 'Guardar precio' : 'Listo'"
      ok-variant="primary"
      ok-only
      scrollable
      @ok="savePriceFromModal"
      @hidden="onPriceModalHidden"
    >
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="money" /></span>
        <h5 class="app-modal-header__title">Precio — {{ (suppliersProducts.find(i => i.id === selectedSupplierProductId) || {}).supplier_name || '' }}</h5>
      </div>
      <div v-if="form.code" class="supplier-modal-code-row mb-3">
        <span class="small text-muted mr-2">Código:</span>
        <code class="supplier-modal-code">{{ form.code }}</code>
      </div>

      <!-- Precio de lista (siempre visible) -->
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div class="modal-section-label mb-0">Precio de lista</div>
        <b-form-checkbox v-model="modal.priceWithIva" switch size="sm" class="mb-0" @change="onPriceWithIvaToggle">
          <small class="text-muted">Ingresar c/IVA</small>
        </b-form-checkbox>
      </div>
      <b-form-group class="mb-3">
        <b-input-group prepend="$" size="lg">
          <formatted-number-input
            v-if="!modal.priceWithIva"
            v-model="form.price_list"
            size="lg"
            @input="recalculateSalePrices()"
          ></formatted-number-input>
          <formatted-number-input
            v-else
            v-model="form.price_list_iva"
            size="lg"
            @input="onPriceListIvaInput()"
          ></formatted-number-input>
          <b-input-group-append is-text>
            <small class="text-muted">{{ modal.priceWithIva ? 'c/IVA' : 's/IVA' }}</small>
          </b-input-group-append>
        </b-input-group>
        <small class="text-muted" style="font-size:0.72rem;">
          <template v-if="modal.priceWithIva">= {{ money(form.price_list) }} s/IVA</template>
          <template v-else>= {{ money(form.price_list_iva) }} c/IVA</template>
        </small>
      </b-form-group>

      <!-- Desplegable de desglose -->
      <button type="button" class="modal-breakdown-toggle" @click="modal.priceBreakdown = !modal.priceBreakdown">
        <span class="modal-breakdown-toggle__label">
          <AppIcon name="settings" class="mr-1" />
          Desglose de precios
          <span v-if="hasCostAdjustments && !modal.priceBreakdown" class="modal-breakdown-toggle__status">con ajustes</span>
        </span>
        <AppIcon :name="modal.priceBreakdown ? 'chevron-up' : 'chevron-down'" class="modal-breakdown-toggle__icon" />
      </button>

      <!-- Desglose expandido -->
      <div v-if="modal.priceBreakdown" class="mt-3">
        <!-- Adicional fijo antes de descuentos -->
        <b-row class="mb-2">
          <b-col cols="6">
            <b-form-group label="Adicional fijo ($)" label-size="sm" class="mb-0">
              <b-input-group prepend="$" size="sm">
                <formatted-number-input v-model="form.additional" @input="recalculateSalePrices()"></formatted-number-input>
              </b-input-group>
              <small class="text-muted" style="font-size:0.68rem;">Se suma antes de los descuentos</small>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Descuentos -->
        <div class="modal-section-label mt-2">Descuentos</div>
        <b-row class="mb-1">
          <b-col cols="4">
            <b-form-group label="Dto 1 %" label-size="sm" class="mb-0">
              <b-input-group append="%" size="sm">
                <formatted-number-input v-model="form.percentage_discount" @input="recalculateSalePrices()"></formatted-number-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Dto 2 %" label-size="sm" class="mb-0">
              <b-input-group append="%" size="sm">
                <formatted-number-input v-model="form.percentage_discount_1" @input="recalculateSalePrices()"></formatted-number-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Dto 3 %" label-size="sm" class="mb-0">
              <b-input-group append="%" size="sm">
                <formatted-number-input v-model="form.percentage_discount_2" @input="recalculateSalePrices()"></formatted-number-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
        <small class="text-muted d-block mb-2" style="font-size:0.68rem;">Cada descuento se aplica sobre el precio resultante del anterior</small>

        <!-- Adicionales % -->
        <div class="modal-section-label">Adicionales %</div>
        <b-row class="mb-1">
          <b-col cols="6">
            <b-form-group label="% Adic 1" label-size="sm" class="mb-0">
              <b-input-group append="%" size="sm">
                <formatted-number-input v-model="form.additional_percentage" @input="recalculateSalePrices()"></formatted-number-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="% Adic 2" label-size="sm" class="mb-0">
              <b-input-group append="%" size="sm">
                <formatted-number-input v-model="form.additional_percentage_1" @input="recalculateSalePrices()"></formatted-number-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Adicional fijo final -->
        <b-row class="mt-2 mb-3">
          <b-col cols="6">
            <b-form-group label="Adicional fijo final ($)" label-size="sm" class="mb-0">
              <b-input-group prepend="$" size="sm">
                <formatted-number-input v-model="form.additional_1" @input="recalculateSalePrices()"></formatted-number-input>
              </b-input-group>
              <small class="text-muted" style="font-size:0.68rem;">Se suma al precio neto final</small>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Breakdown paso a paso -->
        <div class="cost-steps-box">
          <div class="cost-steps-row">
            <span class="cost-steps-label">Lista</span>
            <span></span>
            <span class="cost-steps-value">{{ money(toNumber(form.price_list)) }}</span>
          </div>
          <div v-for="step in costSteps" :key="step.key" class="cost-steps-row">
            <span class="cost-steps-label text-muted">{{ step.label }}</span>
            <span class="cost-steps-sign" :class="step.positive ? 'text-success' : 'text-danger'">{{ step.positive ? '+' : '−' }}</span>
            <span class="cost-steps-value" :class="step.positive ? 'text-success' : 'text-danger'">
              {{ money(Math.abs(step.delta)) }} → {{ money(step.running) }}
            </span>
          </div>
          <div class="cost-steps-row cost-steps-final">
            <span class="cost-steps-label font-weight-bold">Costo s/IVA</span>
            <span></span>
            <span class="cost-steps-value font-weight-bold" style="color:#78350f;">{{ money(selectedCost) }}</span>
          </div>
          <div class="cost-steps-row cost-steps-final cost-steps-iva">
            <span class="cost-steps-label font-weight-bold">Costo c/IVA ({{ ivaAliquot }}%)</span>
            <span></span>
            <span class="cost-steps-value font-weight-bold">{{ money(selectedCostWithIva) }}</span>
          </div>
        </div>
      </div>

      <!-- Costo final simplificado (cuando desglose cerrado) -->
      <div v-else class="base-cost-bar mt-2">
        <div v-if="hasCostAdjustments" class="base-cost-bar__hint-row">
          <span
            id="supplier-price-detail-tooltip"
            class="base-cost-bar__hint"
            tabindex="0"
          >
            <AppIcon name="info-circle" class="mr-1" />Detalle del precio
          </span>
          <b-tooltip
            target="supplier-price-detail-tooltip"
            triggers="hover focus"
            placement="topright"
            custom-class="supplier-price-detail-tooltip"
          >
            <div class="price-detail-tooltip">
              <div class="price-detail-tooltip__title">Detalle del precio</div>
              <div class="price-detail-tooltip__row">
                <span>Lista</span>
                <strong>{{ money(form.price_list) }}</strong>
              </div>
              <div v-for="step in costSteps" :key="'tooltip-' + step.key" class="price-detail-tooltip__row">
                <span>{{ step.label }}</span>
                <strong :class="step.positive ? 'text-success' : 'text-danger'">
                  {{ step.positive ? '+' : '-' }}{{ money(Math.abs(step.delta)) }}
                </strong>
              </div>
              <div class="price-detail-tooltip__total">
                <span>s/IVA</span>
                <strong>{{ money(selectedCost) }}</strong>
              </div>
              <div class="price-detail-tooltip__total price-detail-tooltip__total--iva">
                <span>c/IVA</span>
                <strong>{{ money(selectedCostWithIva) }}</strong>
              </div>
            </div>
          </b-tooltip>
        </div>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <span class="base-cost-bar__label base-cost-bar__label--iva">Costo c/IVA</span>
          <span class="base-cost-bar__final base-cost-bar__final--iva">{{ money(selectedCostWithIva) }}</span>
        </div>
        <div class="d-flex align-items-center justify-content-between">
          <span class="base-cost-bar__label text-muted" style="font-size:0.76rem;font-weight:600;">Costo s/IVA</span>
          <span class="base-cost-bar__final text-muted" style="font-size:0.9rem;">{{ money(selectedCost) }}</span>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="modal.category" centered size="md" button-size="lg" ok-title="Guardar" cancel-title="Cancelar" ok-variant="primary" @ok="saveNewCategory">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="tags" /></span>
        <h5 class="app-modal-header__title">{{ categoryModalTitle }}</h5>
      </div>
      <b-form-group :label="modal.categoryType === 'category' ? 'Nombre de la categoría' : 'Nombre de la subcategoría'">
        <b-form-input v-model="newCategoryName" autofocus></b-form-input>
      </b-form-group>
      <b-alert v-if="modal.categoryType === 'subcategory'" variant="info" show class="py-2 mb-0">
        Se creará dentro de <strong>{{ form.category || 'la categoría seleccionada' }}</strong> al guardar el producto.
      </b-alert>
    </b-modal>

    <!-- Quick-add a Orden de Compra -->
    <b-modal
      v-model="modal.quickAddOrder"
      centered
:ok-title="quickAddOrder.loading ? 'Agregando...' : 'Agregar'"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="quickAddOrder.loading || quickAddOrder.loadingOrder || !quickAddOrder.quantity || quickAddOrder.quantity <= 0"
      @ok.prevent="confirmQuickAddOrder()"
    >
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="cart-plus" /></span>
        <h5 class="app-modal-header__title">{{ quickAddOrder.supplierItem ? 'OC — ' + quickAddOrder.supplierItem.supplier_name : 'Orden de Compra' }}</h5>
      </div>
      <div v-if="quickAddOrder.supplierItem">

        <!-- Producto -->
        <div class="qao-product-row mb-3">
          <AppIcon name="box-seam" class="text-muted mr-2 flex-shrink-0" />
          <div>
            <div class="font-weight-bold">{{ form.name }}</div>
            <small class="text-muted">{{ form.barcode || form.code_system || '' }}</small>
          </div>
        </div>

        <!-- Última entrega de este proveedor -->
        <div class="qao-info-row mb-3">
          <AppIcon name="truck" class="text-muted mr-2 flex-shrink-0" />
          <div>
            <div class="small text-muted">Última entrega de {{ quickAddOrder.supplierItem.supplier_name }}</div>
            <span
              class="small font-weight-bold"
              :class="dateAgoClass(quickAddOrder.supplierItem.last_purchase)"
            >
              {{ quickAddOrder.supplierItem.last_purchase ? daysAgo(quickAddOrder.supplierItem.last_purchase) + ' (' + formatDate(quickAddOrder.supplierItem.last_purchase) + ')' : 'Sin entregas registradas' }}
            </span>
          </div>
        </div>

        <hr class="my-3">

        <!-- Estado de OC abierta -->
        <div v-if="quickAddOrder.loadingOrder" class="text-center text-muted py-2 mb-3">
          <b-spinner small class="mr-2"></b-spinner>
          <small>Buscando órdenes abiertas...</small>
        </div>

        <div v-else-if="quickAddOrder.existingOrder && !quickAddOrder.forceNew" class="mb-3">
          <div class="qao-order-badge mb-2">
            <AppIcon name="file-earmark-text" class="mr-1" />
            OC #{{ quickAddOrder.existingOrder.id }} abierta
          </div>
          <div v-if="quickAddOrder.existingItem" class="qao-existing-item mb-2">
            <AppIcon name="exclamation-circle" class="mr-1 text-warning" />
            <span>
              Ya figura en esta OC:
              <strong>{{ formatStockQty(quickAddOrder.existingItem.quantity) }} {{ quickAddOrder.existingItem.unit_measurement_reference || quickAddOrderUnitName }}</strong>
              pedidas,
              <strong>{{ formatStockQty(quickAddOrder.existingItem.quantity_pending) }}</strong>
              pendientes de entrega.
            </span>
          </div>
          <small class="text-muted" v-else>Este producto aún no está en esa OC.</small>
        </div>

        <div v-else-if="!quickAddOrder.loadingOrder && !quickAddOrder.existingOrder" class="mb-3">
          <small class="text-muted">
            <AppIcon name="plus-circle" class="mr-1 text-success" />
            No hay OC abierta — se creará una nueva.
          </small>
        </div>

        <!-- Cantidad -->
        <b-form-group class="mb-2" :label="quickAddOrder.existingItem && !quickAddOrder.forceNew ? 'Cantidad a sumar' : 'Cantidad a pedir'">
          <b-input-group :append="quickAddOrderUnitName">
            <formatted-number-input
              v-model="quickAddOrder.quantity"
              :min="0"
              placeholder="0"
            ></formatted-number-input>
          </b-input-group>
        </b-form-group>

        <!-- Forzar nueva OC -->
        <b-form-checkbox
          v-if="quickAddOrder.existingOrder && !quickAddOrder.loadingOrder"
          v-model="quickAddOrder.forceNew"
          class="mt-3 small"
        >
          Crear nueva OC en lugar de agregar a la existente
        </b-form-checkbox>

        <b-alert v-if="quickAddOrder.loading" variant="info" show class="py-2 mb-0 mt-3">
          <b-spinner small class="mr-2"></b-spinner>
          <small>Procesando...</small>
        </b-alert>
      </div>
    </b-modal>

    <b-modal
      v-model="modal.stockAdjust"
      centered
      size="lg"
      button-size="lg"
      :ok-title="stockAdjustChangedCount ? 'Procesar ' + stockAdjustChangedCount + ' ajuste(s)' : 'Sin cambios'"
      ok-variant="success"
      :ok-disabled="stockAdjustChangedCount === 0 || stockAdjust.loading"
      cancel-title="Cancelar"
      @ok.prevent="processStockAdjustments()"
    >
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="box-seam" /></span>
        <h5 class="app-modal-header__title">Ajuste de stock</h5>
      </div>
      <div v-if="selectedStockUnit" class="mb-4">
        <div class="stock-adjust-unit-context">
          <span class="stock-adjust-unit-context__icon"><AppIcon name="box-seam" /></span>
          <div>
            <div class="stock-adjust-unit-context__label">Unidad de compra usada para el recuento</div>
            <div class="stock-adjust-unit-context__value">
              {{ selectedStockUnit.name || 'Unidad' }}
              <span v-if="selectedStockUnit.reference">· Ref. {{ selectedStockUnit.reference }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-for="(dep, idx) in stockAdjust.deposits" :key="dep.deposit.id" class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong style="font-size:1.05rem;">{{ dep.deposit.name }}</strong>
          <span class="text-muted">Actual: <strong>{{ formatStockQty(dep.current_quantity) }}</strong></span>
        </div>
        <b-input-group size="lg">
          <b-input-group-prepend is-text><span class="text-muted">Stock real</span></b-input-group-prepend>
          <formatted-number-input v-model="stockAdjust.deposits[idx].real_quantity" size="lg"></formatted-number-input>
          <b-input-group-append v-if="selectedStockUnit && selectedStockUnit.reference" is-text>
            <span class="text-muted">{{ selectedStockUnit.reference }}</span>
          </b-input-group-append>
        </b-input-group>
        <div v-if="stockAdjustHasChanged(dep)" class="mt-2">
          <span :class="dep.real_quantity > dep.current_quantity ? 'text-success' : 'text-danger'" style="font-size:0.95rem;">
            <AppIcon :name="dep.real_quantity > dep.current_quantity ? 'arrow-up-circle-fill' : 'arrow-down'" class="mr-1" />
            {{ dep.real_quantity > dep.current_quantity ? 'Ingreso' : 'Baja' }}:
            <strong>{{ formatStockQty(Math.abs(dep.real_quantity - dep.current_quantity)) }}</strong>
          </span>
        </div>
        <hr v-if="idx < stockAdjust.deposits.length - 1" class="mt-3 mb-0">
      </div>

      <b-alert v-if="stockAdjust.loading" variant="info" show class="py-2 mb-0">
        <b-spinner small class="mr-2"></b-spinner>Procesando...
      </b-alert>
    </b-modal>

    <b-modal v-model="modal.camera" hide-footer centered size="lg" @hide="stopCamera()">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="camera" /></span>
        <h5 class="app-modal-header__title">Escanear código</h5>
      </div>
      <div class="text-center">
        <!-- Quagga necesita un contenedor (div) como target: inserta su propio video/canvas adentro -->
        <div ref="cameraContainer" class="camera-preview-container">
          <video v-show="cameraEngine !== 'quagga'" ref="videoElement" autoplay muted playsinline webkit-playsinline class="camera-preview"></video>
        </div>
        <p class="text-muted small mt-2 mb-0">Apuntá la cámara al código de barras</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Param from '@/config/parameters'
  import {
    purchaseCostFromList,
    purchaseCostSteps,
    salePriceFromCost,
    utilityFromSalePrice,
    netFromGross,
    effectiveUtility,
    isUsingDefaultUtility,
    roundMoney as pmRoundMoney,
    roundUtility as pmRoundUtility,
  } from '@/components/inc/productPricing/pricingMath'

  const FieldGroup = {
    props: ['label', 'field', 'labelIcon', 'labelHint'],
    data() {
      return {
        restored: false,
      }
    },
    methods: {
      reset() {
        this.$emit('reset', this.field)
        this.restored = true
        setTimeout(() => {
          this.restored = false
        }, 1200)
      }
    },
    render(h) {
      let labelContent = this.label
      if(this.labelIcon || this.labelHint) {
        labelContent = h('span', { class: 'field-group-label' }, [
          this.labelIcon ? h('b-icon', { props: { icon: this.labelIcon }, class: 'mr-1' }) : null,
          h('span', this.label),
          this.labelHint ? h('small', { class: 'text-muted font-weight-normal ml-1' }, ['— ' + this.labelHint]) : null,
        ])
      }

      return h('b-form-group', {
        scopedSlots: {
          label: () => labelContent,
        },
        props: { label: this.label },
      }, [
        h('b-input-group', [
          this.$slots.default,
          h('b-input-group-append', [
            h('b-button', {
              props: { variant: this.restored ? 'info' : 'outline-secondary' },
              class: { 'field-reset-restored': this.restored },
              attrs: { title: 'Restablecer valor anterior' },
              on: { click: this.reset }
            }, [
              h('b-icon', { props: { icon: this.restored ? 'check2' : 'arrow-counterclockwise' } })
            ])
          ])
        ])
      ])
    }
  }

  const SmartSelect = {
    props: {
      options: { type: Array, default: () => [] },
      value: { default: null },
      label: { type: String, default: 'text' },
      clearable: { type: Boolean, default: true },
      disabled: { type: Boolean, default: false },
    },
    data() {
      return { isMobile: false }
    },
    created() {
      this.isMobile = window.matchMedia('(pointer: coarse)').matches
    },
    computed: {
      nativeValue() {
        return this.value != null ? String(this.value.value) : ''
      }
    },
    methods: {
      onNativeChange(e) {
        const strVal = e.target.value
        if (!strVal) { this.$emit('input', null); return }
        const opt = this.options.find(o => String(o.value) === strVal) || null
        this.$emit('input', opt)
      },
      onBlur() {
        this.$emit('blur')
      }
    },
    render(h) {
      if (this.isMobile) {
        return h('select', {
          class: 'form-control smart-select-native',
          domProps: { value: this.nativeValue, disabled: this.disabled },
          on: { change: this.onNativeChange, blur: this.onBlur }
        }, this.options.map(opt =>
          h('option', { attrs: { value: String(opt.value) } }, [opt[this.label]])
        ))
      }
      return h('v-select', {
        props: { options: this.options, value: this.value, label: this.label,
                 clearable: this.clearable, disabled: this.disabled },
        on: { input: val => this.$emit('input', val), 'search:blur': this.onBlur }
      })
    }
  }

  const FormattedNumberInput = {
    props: {
      value: { default: 0 },
      disabled: { type: Boolean, default: false },
      size: { type: String, default: '' },
      placeholder: { type: String, default: '' },
      inputClass: { type: String, default: '' },
    },
    data() {
      return { displayValue: '', focused: false }
    },
    watch: {
      value(val) {
        if (!this.focused) this.displayValue = this.fmt(val)
      }
    },
    created() {
      this.displayValue = this.fmt(this.value)
    },
    methods: {
      fmt(val) {
        const num = parseFloat(val)
        if (isNaN(num)) return ''
        return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 8 }).format(num)
      },
      parse(str) {
        const cleaned = String(str || '').replace(/\./g, '').replace(',', '.')
        const num = parseFloat(cleaned)
        return isNaN(num) ? 0 : num
      },
      onFocus(e) {
        this.focused = true
        const el = e.target
        const num = parseFloat(this.value)
        const raw = isNaN(num) || num === 0 ? '' : String(num).replace('.', ',')
        el.value = raw
        this.displayValue = raw
        this.$nextTick(() => el.select())
      },
      onInput(e) {
        this.displayValue = e.target.value
        this.$emit('input', this.parse(e.target.value))
      },
      onKeydown(e) {
        const allowed = ['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End']
        if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return
        if (/^\d$/.test(e.key)) return
        if ((e.key === ',' || e.key === '.') && !e.target.value.includes(',')) {
          if (e.key === '.') {
            e.preventDefault()
            const p = e.target.selectionStart
            const v = e.target.value
            const nv = v.slice(0, p) + ',' + v.slice(e.target.selectionEnd)
            e.target.value = nv
            e.target.setSelectionRange(p + 1, p + 1)
            this.displayValue = nv
          }
          return
        }
        if (e.key === '-' && e.target.selectionStart === 0 && !e.target.value.includes('-')) return
        e.preventDefault()
      },
      onBlur(e) {
        this.focused = false
        const num = this.parse(e.target.value)
        this.$emit('input', num)
        this.displayValue = this.fmt(num)
        this.$emit('blur', e)
      },
    },
    render(h) {
      const classes = ['form-control']
      if (this.size) classes.push(`form-control-${this.size}`)
      if (this.inputClass) classes.push(this.inputClass)
      return h('input', {
        domProps: { value: this.displayValue, disabled: this.disabled },
        class: classes,
        attrs: { type: 'text', inputmode: 'decimal', placeholder: this.placeholder,
                 autocomplete: 'off', autocorrect: 'off', autocapitalize: 'off', spellcheck: 'false' },
        on: { focus: this.onFocus, input: this.onInput, keydown: this.onKeydown, blur: this.onBlur }
      })
    }
  }

  export default {
    components: {
      FieldGroup,
      FormattedNumberInput,
      SmartSelect,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.COSTOS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudProductSuppliersByBarcode',
          elements: {}
        },
        parameters: {
          enableProductShortName: Helper.hasParametersAccess(Param.P76),
          highPrecisionPricing: Helper.hasParametersAccess(Param.P75),
        },
        searchMode: 'barcode',
        barcode: '',
        barcodeEditEnabled: false,
        codeEditEnabled: {
          code: false,
          code_system: false,
        },
        manualSearch: {
          query: '',
          results: [],
          loading: false,
          show: false,
          activeIdx: -1,
          focusTarget: 'input',
          _timer: null,
        },
        product: null,
        productExists: false,
        createdInSession: false,
        stockAdjustedInSession: false,
        guideExpanded: false,
        formReady: false,
        searchError: null,
        suppliersProducts: [],
        savedSupplierCosts: [],
        priceModalSnapshot: null,
        priceModalSaveRequested: false,
        selectedSupplierProductId: 0,
        newSupplierId: 0,
        supplierSelectionPending: false,
        supplierConfirmPending: false,
        originalForm: {},
        options: {
          categories: [],
          suppliers: [],
          unit_measurements: [],
          price_lists: [],
          iva_conditions: [],
        },
        form: {},
        loading: {
          search: false,
          save: false,
          options: false,
          default: false,
          addSupplier: false,
          remove: false,
        },
        modal: {
          camera: false,
          category: false,
          categoryType: 'category',
          supplierPrice: false,
          priceBreakdown: false,
          priceWithIva: false,
          quickAddOrder: false,
          stockAdjust: false,
        },
        stockAdjust: {
          deposits: [],
          loading: false,
        },
        quickAddOrder: {
          loading: false,
          loadingOrder: false,
          supplierItem: null,
          quantity: 1,
          forceNew: false,
          existingOrder: null,
          existingItem: null,
        },
        newCategoryName: '',
        cameraStream: null,
        cameraDetector: null,
        cameraInterval: null,
        quaggaInstance: null,
        cameraEngine: null,
        cameraSupported: true,
        focusMode: false,
        isMobileLayout: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
        mobilePanels: {
          suppliers: false,
          priceLists: false,
          stock: false,
        },
        sections: {
          detail: true,
          suppliers: true,
          priceLists: true,
          stock: true,
        },
        stock: {
          byDeposit: [],
          byDepositRaw: [],
          unitMeasurements: [],
          unitMeasurement: null,
          selectedUnitId: null,
          loading: false,
        },
      }
    },
    computed: {
      requiredCreationSteps() {
        return this.creationSteps.filter(step => step.required)
      },
      completedRequiredCreationSteps() {
        return this.requiredCreationSteps.filter(step => step.done).length
      },
      creationStatus() {
        if(this.requiredCreationSteps.slice(0, 2).some(step => !step.done)) return 'danger'
        if(this.requiredCreationSteps.some(step => !step.done)) return 'warning'
        return 'complete'
      },
      nextCreationStep() {
        return this.requiredCreationSteps.find(step => !step.done) || null
      },
      creationSteps() {
        const dataComplete = !!(
          this.form.name && this.form.category && this.form.subcategory &&
          (this.form.barcode || this.form.code_system) &&
          this.form.unit_measurement_purchase_id && this.form.unit_measurement_sale_id && this.form.iva_condition_id
        )
        const savedCostExists = this.savedSupplierCosts.some(item => this.toNumber(item.cost) > 0)
        return [
          { label: 'Cargar datos del producto', done: dataComplete, required: true },
          { label: 'Agregar el o los proveedores que lo venden', done: this.suppliersProducts.length > 0, required: true },
          { label: 'Cargar precio de costo', done: savedCostExists, required: true },
          { label: 'Configurar precios de venta', done: savedCostExists && this.form.lists.some(list => list.add && list.id > 0), required: true },
          { label: 'Ajustar stock', done: this.stockAdjustedInSession, required: false },
        ]
      },
      categoryModalTitle() {
        return this.modal.categoryType === 'category' ? 'Nueva categoría' : 'Nueva subcategoría'
      },
      categoryOptions() {
        let items = [{ value: '', text: 'Seleccionar categoría' }]
        this.options.categories.forEach(item => items.push({ value: item.name, text: item.name }))
        if(this.form.category && !this.options.categories.find(item => item.name === this.form.category)) {
          items.push({ value: this.form.category, text: this.form.category })
        }
        items.push({ value: '__new__', text: 'Crear nueva categoría...' })
        return items
      },
      subcategoryOptions() {
        let items = [{ value: '', text: 'Seleccionar subcategoría' }]
        let category = this.options.categories.find(item => item.name === this.form.category)
        if(category && category.subcategorys) {
          category.subcategorys.forEach(item => items.push({ value: item.name, text: item.name }))
        }
        if(this.form.subcategory && !items.find(item => item.value === this.form.subcategory)) {
          items.push({ value: this.form.subcategory, text: this.form.subcategory })
        }
        items.push({ value: '__new__', text: 'Crear nueva subcategoría...' })
        return items
      },
      availableSupplierOptions() {
        let linkedIds = this.suppliersProducts.map(item => item.suppliers_id)
        let options = this.options.suppliers
          .filter(item => linkedIds.indexOf(item.id) === -1)
          .map(item => ({
            value: item.id,
            text: (item.code ? item.code + ' - ' : '') + item.name
          }))

        return [{ value: 0, text: options.length ? 'Seleccionar proveedor' : 'No hay proveedores para agregar' }].concat(options)
      },
      unitOptions() {
        return [{ value: 0, text: 'Seleccionar unidad' }].concat(this.options.unit_measurements.map(item => ({
          value: item.id,
          text: item.name + (item.reference ? ' (' + item.reference + ')' : '')
        })))
      },
      ivaOptions() {
        return [{ value: 0, text: 'Seleccionar condición fiscal' }].concat(this.options.iva_conditions.map(item => ({
          value: item.id,
          text: item.name
        })))
      },
      baseCost() {
        return this.defaultCostDetail.price_cost
      },
      ivaAliquot() {
        let cond = this.options.iva_conditions.find(i => i.id === this.form.iva_condition_id)
        return cond ? this.toNumber(cond.aliquot || cond.percentage) : 0
      },
      selectedCostWithIva() {
        return this.roundMoney(this.selectedCost * (1 + this.ivaAliquot / 100))
      },
      selectedCost() {
        return purchaseCostFromList({
          priceList: this.form.price_list,
          additional: this.form.additional,
          percentageDiscount: this.form.percentage_discount,
          percentageDiscount1: this.form.percentage_discount_1,
          percentageDiscount2: this.form.percentage_discount_2,
          additionalPercentage: this.form.additional_percentage,
          additionalPercentage1: this.form.additional_percentage_1,
          additional1: this.form.additional_1,
        })
      },
      defaultSupplierProduct() {
        return this.suppliersProducts.find(item => item.default) || null
      },
      hasCostAdjustments() {
        return [
          'additional',
          'percentage_discount',
          'percentage_discount_1',
          'percentage_discount_2',
          'additional_percentage',
          'additional_percentage_1',
          'additional_1',
        ].some(field => this.toNumber(this.form[field]) > 0)
      },
      defaultCostDetail() {
        let item = this.defaultSupplierProduct
        if(!item) {
          return {
            price_list: 0,
            discount: 0,
            additional: 0,
            price_cost: 0,
          }
        }

        if(item.id === this.selectedSupplierProductId) {
          return {
            price_list: this.toNumber(this.form.price_list),
            discount: this.roundMoney(this.toNumber(this.form.percentage_discount) + this.toNumber(this.form.percentage_discount_1) + this.toNumber(this.form.percentage_discount_2)),
            additional: this.roundMoney(this.toNumber(this.form.additional_percentage) + this.toNumber(this.form.additional_percentage_1)),
            price_cost: this.selectedCost,
          }
        }

        return {
          price_list: this.toNumber(item.price_list),
          discount: this.roundMoney(this.toNumber(item.percentage_discount) + this.toNumber(item.percentage_discount_1) + this.toNumber(item.percentage_discount_2)),
          additional: this.roundMoney(this.toNumber(item.additional_percentage) + this.toNumber(item.additional_percentage_1)),
          price_cost: this.toNumber(item.price_cost),
        }
      },
      costSteps() {
        return purchaseCostSteps({
          priceList: this.form.price_list,
          additional: this.form.additional,
          percentageDiscount: this.form.percentage_discount,
          percentageDiscount1: this.form.percentage_discount_1,
          percentageDiscount2: this.form.percentage_discount_2,
          additionalPercentage: this.form.additional_percentage,
          additionalPercentage1: this.form.additional_percentage_1,
          additional1: this.form.additional_1,
        })
      },
      productDetailFields() {
        let fields = [
          'name',
          'category',
          'subcategory',
          'unit_measurement_purchase_id',
          'unit_measurement_sale_id',
          'unit_measurement_sale_conversion',
          'iva_condition_id',
          'weighable',
        ]
        if(this.parameters.enableProductShortName) {
          fields.push('name_short')
        }
        fields.push('barcode')
        if(this.showProductCodeFields) {
          fields.push('code_system', 'code')
        }
        return fields
      },
      showProductCodeFields() {
        return !this.form.barcode
      },
      productDetailDirty() {
        if(!this.formReady || !this.originalForm) return false
        return this.productDetailFields.some(field => {
          if(field === 'barcode' || field === 'code' || field === 'code_system') {
            return this.codesAreDifferent(this.form[field], this.originalForm[field])
          }
          return this.valuesAreDifferent(this.form[field], this.originalForm[field])
        })
      },
      ivaDirty() {
        if(!this.formReady || !this.originalForm) return false
        return this.valuesAreDifferent(this.form.iva_condition_id, this.originalForm.iva_condition_id)
      },
      priceListsReadOnly() {
        return this.ivaDirty
      },
      showSuppliersPanel() {
        return !this.isMobileLayout || this.mobilePanels.suppliers
      },
      showPriceListsPanel() {
        return !this.isMobileLayout || this.mobilePanels.priceLists
      },
      showStockPanel() {
        return !this.isMobileLayout || this.mobilePanels.stock
      },
      productControlsStock() {
        if(!this.product) return true
        return this.product.control_stock !== false && this.product.control_stock !== 0
      },
      selectedStockUnit() {
        return this.stock.unitMeasurements.find(u => u.id == this.stock.selectedUnitId) || this.stock.unitMeasurement
      },
      stockUnitOptions() {
        return this.stock.unitMeasurements.map(u => ({
          value: u.id,
          text: (u.reference || u.name) + ' — ' + (u.type === 'sale' ? 'venta' : 'compra'),
        }))
      },
      stockTotal() {
        return this.stock.byDeposit.reduce((sum, dep) => sum + this.toNumber(dep.current_quantity), 0)
      },
      stockAdjustChangedCount() {
        return this.stockAdjust.deposits.filter(d => Math.abs(this.toNumber(d.real_quantity) - this.toNumber(d.current_quantity)) >= 0.00005).length
      },
      quickAddOrderUnitName() {
        const item = this.quickAddOrder.supplierItem
        const unitId = (item && item.unit_measurement_purchase_id) || this.form.unit_measurement_purchase_id
        const unit = this.options.unit_measurements && this.options.unit_measurements.find(u => u.id == unitId)
        return unit ? (unit.reference || unit.name) : '—'
      },
    },
    watch: {
      sections: {
        deep: true,
        handler() { this.savePreferences() }
      },
      searchMode() { this.savePreferences() },
      'manualSearch.query'() {
        this.manualSearch.focusTarget = 'input'
        this.manualSearch.activeIdx = -1
      },
      barcode(val) {
        if (!val) return
        if (this.searchMode !== 'barcode') return
        clearTimeout(this._barcodeTimer)
        this._barcodeTimer = setTimeout(() => {
          if (this.barcode && !this.formReady && !this.loading.search && this.searchMode === 'barcode') {
            this.searchProduct()
          }
        }, 180)
      }
    },
    created() {
      Helper.hasAccessView(this.access)
      this.resetForm()
      this.loadLightOptions()
      this.loadFormOptions()
      this.loadPreferences()
    },
    mounted() {
      this.disableFocusMode()
      this.updateMobileLayout()
      window.addEventListener('resize', this.updateMobileLayout)
      this.focusSearch()
    },
    beforeDestroy() {
      this.stopCamera()
      window.removeEventListener('resize', this.updateMobileLayout)
      this.exitBrowserFullscreen()
      this.disableFocusMode()
    },
    deactivated() {
      this.stopCamera()
      this.exitBrowserFullscreen()
      this.disableFocusMode()
    },
    methods: {
      confirmDialog(message, options) {
        const danger = !!options.danger
        const icon = options.icon || (danger ? 'warning' : 'help')
        const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true,
        })
      },
      async goBack() {
        await this.exitBrowserFullscreen()
        this.disableFocusMode()
        this.$router.push({ name: 'CostsStaffDashboard' })
      },
      toggleMobilePanel(panel) {
        if (!this.mobilePanels.hasOwnProperty(panel)) return
        this.mobilePanels[panel] = !this.mobilePanels[panel]
        if (panel === 'suppliers' && !this.mobilePanels[panel]) {
          this.clearSupplierSelection()
        }
      },
      clearSupplierSelection() {
        if (!this.selectedSupplierProductId) return
        this.selectedSupplierProductId = 0
      },
      updateMobileLayout() {
        if (typeof window === 'undefined') return
        this.isMobileLayout = window.innerWidth < 768
      },
      async toggleFocusMode() {
        const enable = !this.focusMode
        if (enable) {
          this.enableFocusMode()
          await this.requestBrowserFullscreen()
        } else {
          await this.exitBrowserFullscreen()
          this.disableFocusMode()
        }
      },
      enableFocusMode() {
        this.focusMode = true
        if (typeof document === 'undefined') return
        if (document.body) {
          document.body.classList.add('barcode-cost-focus-mode')
        }
      },
      disableFocusMode() {
        this.focusMode = false
        if (typeof document === 'undefined') return
        if (document.body) {
          document.body.classList.remove('barcode-cost-focus-mode')
        }
      },
      async requestBrowserFullscreen() {
        if (typeof document === 'undefined') return
        if (document.fullscreenElement) return
        const target = document.documentElement
        if (target && typeof target.requestFullscreen === 'function') {
          try {
            await target.requestFullscreen()
          } catch (error) {
            if (error) return false
          }
        }
      },
      async exitBrowserFullscreen() {
        if (typeof document === 'undefined') return
        if (!document.fullscreenElement) return
        if (typeof document.exitFullscreen === 'function') {
          try {
            await document.exitFullscreen()
          } catch (error) {
            if (error) return false
          }
        }
      },
      resetForm() {
        this.barcodeEditEnabled = false
        this.codeEditEnabled = { code: false, code_system: false }
        this.form = {
          type: 'NUEVO',
          id: 0,
          products_id: 0,
          suppliers_id: 0,
          code: '',
          code_system: '',
          barcode: '',
          name: '',
          name_short: '',
          category: '',
          subcategory: '',
          line: '',
          subline: '',
          price_list: 0,
          price_list_iva: 0,
          percentage_discount: 0,
          percentage_discount_1: 0,
          percentage_discount_2: 0,
          additional: 0,
          additional_1: 0,
          additional_percentage: 0,
          additional_percentage_1: 0,
          depth: 0,
          long: 0,
          height: 0,
          weight: 0,
          unit_measurement_purchase_id: 0,
          unit_measurement_sale_id: 0,
          unit_measurement_sale_conversion: 1,
          iva_condition_id: 0,
          weighable: false,
          reference_external: null,
          lists: [],
        }
        this.setOriginalForm()
      },
      setOriginalForm() {
        this.originalForm = JSON.parse(JSON.stringify(this.form))
      },
      resetSearch() {
        this.barcode = ''
        this.barcodeEditEnabled = false
        this.codeEditEnabled = { code: false, code_system: false }
        this.manualSearch.query = ''
        this.manualSearch.results = []
        this.manualSearch.show = false
        this.manualSearch.activeIdx = -1
        this.manualSearch.focusTarget = 'input'
        this.product = null
        this.productExists = false
        this.createdInSession = false
        this.stockAdjustedInSession = false
        this.guideExpanded = false
        this.formReady = false
        this.searchError = null
        this.suppliersProducts = []
        this.savedSupplierCosts = []
        this.priceModalSnapshot = null
        this.priceModalSaveRequested = false
        this.selectedSupplierProductId = 0
        this.newSupplierId = 0
        this.supplierSelectionPending = false
        this.supplierConfirmPending = false
        this.resetForm()
        this.resetStock()
        this.focusSearch()
      },
      focusSearch() {
        if (this.isMobileLayout) return
        this.$nextTick(() => {
          if (this.searchMode === 'manual') {
            if (this.$refs.manualSearchInput) this.$refs.manualSearchInput.$el.focus()
          } else {
            if (this.$refs.barcodeInput) this.$refs.barcodeInput.$el.focus()
          }
        })
      },
      enableBarcodeEdit() {
        this.barcodeEditEnabled = true
        this.$nextTick(() => {
          const input = this.$refs.productBarcodeInput && this.$refs.productBarcodeInput.$el
          if(input) {
            input.focus()
            input.select()
          }
        })
      },
      enableCodeEdit(field) {
        if(!this.codeEditEnabled.hasOwnProperty(field)) return
        this.codeEditEnabled[field] = true
        this.$nextTick(() => {
          const ref = field === 'code_system' ? 'productCodeSystemInput' : 'productCodeInput'
          const input = this.$refs[ref] && this.$refs[ref].$el
          if(input) {
            input.focus()
            input.select()
          }
        })
      },
      keepBarcodeFocus(e) {
        if (this.isMobileLayout || this.searchMode !== 'barcode' || this.formReady) return
        const barcodeEl = this.$refs.barcodeInput && this.$refs.barcodeInput.$el
        if (barcodeEl && e.target !== barcodeEl) {
          barcodeEl.focus()
        }
      },
      loadLightOptions() {
        serviceAPI.obtenerCondicionesIva().then(response => {
          this.options.iva_conditions = response.data
        }).catch(() => {})
      },
      loadFormOptions() {
        this.loading.options = true
        serviceAPI.obtenerOpcionesProductoCostosPorCodigoBarras()
          .then(response => {
            this.options.categories = response.data.categories || []
            this.options.suppliers = response.data.suppliers || []
            this.options.unit_measurements = response.data.unit_measurements || []
            this.options.price_lists = response.data.price_lists || []
            this.loading.options = false
            this.applyOptionsDefaults()
          })
          .catch(error => {
            this.loading.options = false
            this.$awn.alert(Error.showError(error))
          })
      },
      applyOptionsDefaults() {
        if(!this.formReady) return
        let defaultUnit = this.getDefaultUnitId()
        if(!this.form.unit_measurement_purchase_id) {
          this.form.unit_measurement_purchase_id = defaultUnit
        }
        if(!this.form.unit_measurement_sale_id) {
          this.form.unit_measurement_sale_id = this.form.unit_measurement_purchase_id
        }
        if(!this.form.iva_condition_id) {
          this.form.iva_condition_id = this.getDefaultIvaConditionId()
        }
        this.form.lists = this.buildPriceLists(this.product, !this.productExists)
        this.recalculateSalePrices()
        this.setOriginalForm()
      },
      getDefaultUnitId() {
        let unit = this.options.unit_measurements.find(item => {
          let name = (item.name || '').toString().toLowerCase()
          let reference = (item.reference || '').toString().toLowerCase()
          return name === 'unidad' || reference === 'unidad' || reference === 'un'
        })
        return unit ? unit.id : (this.options.unit_measurements.length ? this.options.unit_measurements[0].id : 0)
      },
      getDefaultIvaConditionId() {
        let iva = this.options.iva_conditions.find(item => {
          let name = (item.name || '').toString()
          return name.indexOf('21') !== -1
        })
        return iva ? iva.id : 0
      },
      findOption(options, value) {
        return options.find(item => item.value === value) || null
      },
      selectOption(field, option, callback = null) {
        if(!option) return
        this.form[field] = option.value
        if(callback) {
          callback(option.value)
        }
        this.recalculateSalePrices()
      },
      selectNewSupplier(option) {
        this.newSupplierId = option ? option.value : 0
        this.supplierSelectionPending = !!this.newSupplierId
      },
      clearNewSupplierSelection() {
        this.newSupplierId = 0
        this.supplierSelectionPending = false
      },
      onNewSupplierBlur() {
        this.$nextTick(() => {
          if(this.supplierSelectionPending && this.newSupplierId) this.addSupplierToProduct()
        })
      },
      reloadCurrentProduct() {
        if(this.barcode) {
          this.searchProduct()
          return
        }
        const productId = this.form.products_id
        if(productId) {
          this.loading.search = true
          serviceAPI.cargarProductoCostosPorId({ product_id: productId })
            .then(r => { this.loading.search = false; this.applySearchResult(r.data) })
            .catch(() => { this.loading.search = false })
        } else {
          const code = this.form.code_system || this.form.code
          if(!code) return
          this.loading.search = true
          serviceAPI.buscarProductoCostosPorCodigoBarras({ barcode: code })
            .then(r => { this.loading.search = false; this.applySearchResult(r.data) })
            .catch(() => { this.loading.search = false })
        }
      },
      searchProduct() {
        if (!this.barcode) return
        this.loading.search = true
        this.searchError = null

        serviceAPI.buscarProductoCostosPorCodigoBarras({ barcode: this.barcode })
          .then((response) => {
            this.loading.search = false
            this.applySearchResult(response.data)
          })
          .catch((error) => {
            this.searchError = Error.showError(error)
            this.loading.search = false
          })
      },
      applySearchResult(data) {
        if(!this.formReady) {
          this.createdInSession = !data.exists
          this.stockAdjustedInSession = false
          this.guideExpanded = false
        }
        this.productExists = !!data.exists
        this.product = data.product
        this.suppliersProducts = data.suppliers_products || []
        this.savedSupplierCosts = this.suppliersProducts.map(item => ({ id: item.id, cost: item.price_cost }))
        this.priceModalSnapshot = null
        this.priceModalSaveRequested = false
        this.selectedSupplierProductId = 0

        // Normalizar default: solo uno permitido.
        let target = this.suppliersProducts.find(i => i.default)
        if (!target && this.suppliersProducts.length > 0) target = this.suppliersProducts[0]
        this.suppliersProducts.forEach(i => { i.default = !!(target && i === target) })
        this.resetForm()

        let barcode = data.product ? (data.product.barcode || '') : (data.barcode || '')
        let code = data.product ? (data.product.code || barcode) : barcode
        let defaultUnit = this.getDefaultUnitId()
        this.form.code_system = code
        this.form.barcode = barcode
        this.form.code = barcode || ''
        this.form.name = data.product ? data.product.name : ''
        this.form.name_short = data.product ? data.product.name_short : ''
        this.form.products_id = data.product ? data.product.id : 0
        this.form.category = data.product && data.product.subcategory && data.product.subcategory.category ? data.product.subcategory.category.name : ''
        this.form.subcategory = data.product && data.product.subcategory ? data.product.subcategory.name : ''
        this.form.unit_measurement_purchase_id = data.product && data.product.unit_measurement_purchase_id ? data.product.unit_measurement_purchase_id : defaultUnit
        this.form.unit_measurement_sale_id = data.product && data.product.unit_measurement_sale_id ? data.product.unit_measurement_sale_id : this.form.unit_measurement_purchase_id
        this.form.unit_measurement_sale_conversion = data.product && data.product.unit_measurement_sale_conversion ? data.product.unit_measurement_sale_conversion : 1
        this.form.iva_condition_id = data.product && data.product.erp_iva_condition_id ? data.product.erp_iva_condition_id : this.getDefaultIvaConditionId()
        this.form.weighable = !!(data.product && data.product.weighable)
        this.form.lists = this.buildPriceLists(data.product, !this.productExists)
        this.applyDefaultSupplierToForm()

        this.formReady = true
        this.recalculateSalePrices()
        this.setOriginalForm()
        this.resetStock()
        this.loadStock()
      },
      applyDefaultSupplierToForm() {
        let defaultItem = this.defaultSupplierProduct || this.suppliersProducts[0] || null
        if(!defaultItem) {
          this.form.type = 'NUEVO'
          this.form.id = 0
          this.form.suppliers_id = 0
          this.form.price_list = 0
          this.form.percentage_discount = 0
          this.form.percentage_discount_1 = 0
          this.form.percentage_discount_2 = 0
          this.form.additional = 0
          this.form.additional_1 = 0
          this.form.additional_percentage = 0
          this.form.additional_percentage_1 = 0
          if(!this.form.code) {
            this.form.code = this.form.barcode
          }
          return
        }

        this.form.type = defaultItem.id > 0 ? 'EDITAR' : 'NUEVO'
        this.form.id = defaultItem.id || 0
        this.form.suppliers_id = defaultItem.suppliers_id
        if(this.form.barcode || !this.form.code) {
          this.form.code = defaultItem.code || this.form.barcode
        }
        this.form.price_list = this.toNumber(defaultItem.price_list)
        this.form.percentage_discount = this.toNumber(defaultItem.percentage_discount)
        this.form.percentage_discount_1 = this.toNumber(defaultItem.percentage_discount_1)
        this.form.percentage_discount_2 = this.toNumber(defaultItem.percentage_discount_2)
        this.form.additional = this.toNumber(defaultItem.additional)
        this.form.additional_1 = this.toNumber(defaultItem.additional_1)
        this.form.additional_percentage = this.toNumber(defaultItem.additional_percentage)
        this.form.additional_percentage_1 = this.toNumber(defaultItem.additional_percentage_1)
      },
      buildPriceLists(product, includeByDefault = false) {
        let priceMap = {}
        if(product && product.prices) {
          product.prices.forEach(price => {
            priceMap[price.price_list_id] = price
          })
        }

        return this.options.price_lists.map(list => {
          let price = priceMap[list.id]
          let listDefaultUtility = this.toNumber(list.utility)
          let productUtility = price ? this.toNumber(price.utility) : 0
          return {
            id: price ? price.id : 0,
            add: price ? true : includeByDefault,
            price_list_id: list.id,
            name: list.name,
            utility: productUtility,
            default_utility: listDefaultUtility,
            using_default_utility: productUtility === 0 && listDefaultUtility > 0,
            desc: price ? this.toNumber(price.desc) : 0,
            sale_price: 0,
            sale_price_iva: 0,
            saving: false,
            saved: false,
            show_calculation: false,
            manualSaveRequested: false,
          }
        })
      },
      selectSupplierProduct(item, updateOriginal = true) {
        this.selectedSupplierProductId = item.id
        this.form.type = item.id > 0 ? 'EDITAR' : 'NUEVO'
        this.form.id = item.id > 0 ? item.id : 0
        this.form.suppliers_id = item.suppliers_id
        this.form.code = item.code
        this.form.price_list = item.price_list
        this.form.percentage_discount = item.percentage_discount
        this.form.percentage_discount_1 = item.percentage_discount_1
        this.form.percentage_discount_2 = item.percentage_discount_2
        this.form.additional = item.additional
        this.form.additional_1 = item.additional_1
        this.form.additional_percentage = item.additional_percentage
        this.form.additional_percentage_1 = item.additional_percentage_1
        this.form.unit_measurement_purchase_id = item.unit_measurement_purchase_id || this.form.unit_measurement_purchase_id
        this.form.iva_condition_id = item.iva_condition || this.form.iva_condition_id
        this.form.reference_external = item.reference_external
        this.recalculateSalePrices()
        if(updateOriginal) {
          this.setOriginalForm()
        }
      },
      openPriceModal(item) {
        this.priceModalSnapshot = { id: item.id, values: Object.assign({}, item) }
        this.priceModalSaveRequested = false
        this.selectSupplierProduct(item)
        this.modal.supplierPrice = true
      },
      onPriceModalHidden() {
        if(this.priceModalSaveRequested || !this.priceModalSnapshot) return
        this.restorePriceModalSnapshot()
      },
      restorePriceModalSnapshot() {
        if(!this.priceModalSnapshot) return
        const item = this.suppliersProducts.find(row => row.id === this.priceModalSnapshot.id)
        if(item) {
          Object.assign(item, this.priceModalSnapshot.values)
          this.selectSupplierProduct(item)
        }
        this.priceModalSnapshot = null
        this.priceModalSaveRequested = false
      },
      savePriceFromModal() {
        if (this.form.type !== 'EDITAR' || !this.form.id) return
        let item = this.suppliersProducts.find(i => i.id === this.selectedSupplierProductId)
        let productId = (item && item.product_id) ? item.product_id : (this.product ? this.product.id : 0)
        const savedCost = this.selectedCost
        this.priceModalSaveRequested = true
        this.loading.save = true
        serviceAPI.updateProducto({
          id: this.form.id,
          product_id: productId,
          price_list: this.form.price_list,
          percentage_discount: this.form.percentage_discount,
          percentage_discount_1: this.form.percentage_discount_1,
          percentage_discount_2: this.form.percentage_discount_2,
          additional: this.form.additional,
          additional_1: this.form.additional_1,
          additional_percentage: this.form.additional_percentage,
          additional_percentage_1: this.form.additional_percentage_1,
          force_price_update: true,
          edit_source: 'crudProductSuppliersByBarcode',
          staff_id: Helper.getStaffId(),
        })
          .then(() => {
            this.loading.save = false
            this.$awn.success('Precio actualizado')
            if(item) {
              item.price_list = this.toNumber(this.form.price_list)
              item.percentage_discount = this.toNumber(this.form.percentage_discount)
              item.percentage_discount_1 = this.toNumber(this.form.percentage_discount_1)
              item.percentage_discount_2 = this.toNumber(this.form.percentage_discount_2)
              item.additional = this.toNumber(this.form.additional)
              item.additional_1 = this.toNumber(this.form.additional_1)
              item.additional_percentage = this.toNumber(this.form.additional_percentage)
              item.additional_percentage_1 = this.toNumber(this.form.additional_percentage_1)
              item.price_cost = savedCost
              item.last_update_price = new Date().toISOString()
              this.savedSupplierCosts = this.savedSupplierCosts.filter(row => row.id !== item.id)
                .concat({ id: item.id, cost: item.price_cost })
            }
            this.priceModalSnapshot = null
            this.priceModalSaveRequested = false
            this.setOriginalForm()
          })
          .catch(error => {
            this.loading.save = false
            this.restorePriceModalSnapshot()
            this.$awn.alert(Error.showError(error))
          })
      },
      setDefault(item) {
        if (!item || item.id <= 0) {
          this.$awn.alert('Guardá el proveedor antes de marcarlo como default')
          return
        }
        this.confirmDialog(
          `¿Cambiar el proveedor default a "${item.supplier_name}"?`,
          { title: 'Cambiar proveedor default', icon: 'star', okTitle: 'Confirmar' }
        ).then(confirmed => {
          if (confirmed) this.doSetDefault(item)
        })
      },
      doSetDefault(item) {
        this.loading.default = true
        serviceAPI.marcarDefault({ id: item.id, edit_source: 'crudProductSuppliersByBarcode', staff_id: Helper.getStaffId() })
          .then(() => {
            this.suppliersProducts.forEach(row => { row.default = row.id === item.id })
            this.loading.default = false
            this.recalculateSalePrices()
            this.$awn.success('Proveedor default actualizado')
            if (this.toNumber(item.price_list) === 0) {
              this.openPriceModal(item)
            }
          })
          .catch(error => {
            this.loading.default = false
            this.$awn.alert(Error.showError(error))
          })
      },
      removeSupplier(item) {
        if (!item || item.id <= 0) return
        if(this.suppliersProducts.length <= 1) {
          this.$awn.alert('No se puede quitar el único proveedor del producto')
          return
        }
        let msg = item.default
          ? `¿Quitar a "${item.supplier_name}"? Es el proveedor default — deberás asignar otro.`
          : `¿Quitar a "${item.supplier_name}" de este producto?`
        this.confirmDialog(msg, {
          title: 'Quitar proveedor',
          okTitle: 'Quitar',
          danger: true,
          icon: 'trash',
        }).then(confirmed => {
          if (!confirmed) return
          this.loading.remove = true
          serviceAPI.deleteProducto({ id: item.id, edit_source: 'crudProductSuppliersByBarcode', staff_id: Helper.getStaffId() })
            .then(() => {
              this.loading.remove = false
              this.$awn.success(`Proveedor ${item.supplier_name} quitado`)
              if (this.selectedSupplierProductId === item.id) {
                this.selectedSupplierProductId = 0
              }
              this.reloadCurrentProduct()
            })
            .catch(error => {
              this.loading.remove = false
              this.$awn.alert(Error.showError(error))
            })
        })
      },
      addSupplierToProduct() {
        if(this.supplierConfirmPending || this.loading.addSupplier) return
        let supplier = this.options.suppliers.find(item => item.id === this.newSupplierId)
        if(!supplier) return
        this.supplierSelectionPending = false

        let exists = this.suppliersProducts.find(item => item.suppliers_id === supplier.id)
        if(exists) {
          this.clearNewSupplierSelection()
          this.$awn.alert(`${supplier.name} ya está vinculado a este producto`)
          return
        }

        if(!this.form.name) {
          this.clearNewSupplierSelection()
          this.$awn.alert('Completá el nombre del producto antes de agregar un proveedor')
          return
        }

        if(!this.form.barcode && !this.form.code) {
          this.clearNewSupplierSelection()
          this.$awn.alert('Ingresá el código del proveedor antes de agregar')
          return
        }

        this.supplierConfirmPending = true
        this.confirmDialog(
          `¿Agregar a "${supplier.name}" como proveedor de este producto?`,
          { title: 'Agregar proveedor', icon: 'plus-circle', okTitle: 'Agregar' }
        ).then(confirmed => {
          if(!confirmed) {
            this.supplierConfirmPending = false
            this.clearNewSupplierSelection()
            return
          }

          const supplierCode = this.form.code || this.form.barcode
          const ownCode = this.form.code_system || supplierCode

          let payload = Object.assign({}, this.form, {
            type: 'NUEVO',
            id: 0,
            suppliers_id: supplier.id,
            code: supplierCode,
            code_system: ownCode,
            price_list: 0,
            percentage_discount: 0,
            percentage_discount_1: 0,
            percentage_discount_2: 0,
            additional: 0,
            additional_1: 0,
            additional_percentage: 0,
            additional_percentage_1: 0,
            skip_supplier_price_update: true,
            edit_source: 'crudProductSuppliersByBarcode',
            staff_id: Helper.getStaffId(),
          })

          if(!this.parameters.enableProductShortName) {
            payload.name_short = payload.name
          }

          this.loading.addSupplier = true
          this.clearNewSupplierSelection()
          serviceAPI.saveProduct(payload)
            .then(() => {
              this.loading.addSupplier = false
              this.supplierConfirmPending = false
              this.$awn.success(`Proveedor ${supplier.name} agregado`)
              this.reloadCurrentProduct()
            })
            .catch(error => {
              this.loading.addSupplier = false
              this.supplierConfirmPending = false
              this.$awn.alert(Error.showError(error))
            })
        }).catch(error => {
          this.supplierConfirmPending = false
          this.$awn.alert(Error.showError(error))
        })
      },
      openQuickAddOrder(supplierItem) {
        this.quickAddOrder.supplierItem = supplierItem
        this.quickAddOrder.quantity = 1
        this.quickAddOrder.loading = false
        this.quickAddOrder.loadingOrder = true
        this.quickAddOrder.forceNew = false
        this.quickAddOrder.existingOrder = null
        this.quickAddOrder.existingItem = null
        this.modal.quickAddOrder = true

        serviceAPI.filtrarOrdenesDeCompra({ suppliers: [supplierItem.suppliers_id], status: ['pending'] }, 1)
          .then(response => {
            const orders = response.data.data || []
            if(orders.length > 0) {
              const order = orders[0]
              this.quickAddOrder.existingOrder = order
              const details = order.details || order.Details || []
              const targetId = parseInt(this.form.products_id) || 0
              this.quickAddOrder.existingItem = targetId > 0
                ? (details.find(d => parseInt(d.products_id) === targetId) || null)
                : null
            }
            this.quickAddOrder.loadingOrder = false
          })
          .catch(() => { this.quickAddOrder.loadingOrder = false })
      },
      confirmQuickAddOrder() {
        const qty = parseFloat(this.quickAddOrder.quantity)
        if(!qty || qty <= 0) {
          this.$awn.alert('Ingresá una cantidad válida')
          return
        }

        const item = this.quickAddOrder.supplierItem
        const unitId = (item && item.unit_measurement_purchase_id) || this.form.unit_measurement_purchase_id
        const today = new Date().toISOString().split('T')[0]

        const newDetail = {
          product: {
            id: this.form.products_id,
            code: this.form.barcode || this.form.code_system,
            name: this.form.name,
            source: 'product',
          },
          unit_measurement: { id: unitId },
          quantity: qty,
          price_cost: this.toNumber(item ? item.price_cost : 0),
          observations: '',
        }

        this.quickAddOrder.loading = true

        const existingOrder = !this.quickAddOrder.forceNew ? this.quickAddOrder.existingOrder : null

        const doRequest = existingOrder
          ? serviceAPI.agregarItemOrdenDeCompra(existingOrder.id, Object.assign({}, newDetail, { force_new: false }))
              .then(r => ({ isNew: false, orderId: existingOrder.id, merged: r.data && r.data.merged }))
          : serviceAPI.agregarOrdenDeCompra({
              suppliers_id: item.suppliers_id,
              date: today,
              date_expected: today,
              staff_id: Helper.getStaffId(),
              detailProducts: [newDetail],
            }).then(r => ({ isNew: true, orderId: r.data.id, merged: false }))

        doRequest
          .then(({ isNew, orderId, merged }) => {
            this.quickAddOrder.loading = false
            this.modal.quickAddOrder = false
            const msg = isNew
              ? `OC #${orderId} creada para ${item.supplier_name}`
              : merged
                ? `Cantidad actualizada en OC #${orderId}`
                : `Agregado a la OC #${orderId} de ${item.supplier_name}`
            this.$awn.success(msg)
          })
          .catch(error => {
            this.quickAddOrder.loading = false
            this.$awn.alert(Error.showError(error))
          })
      },

      onCategoryChange(value) {
        if(value !== '__new__') return
        this.modal.categoryType = 'category'
        this.newCategoryName = ''
        this.modal.category = true
      },
      onSubcategoryChange(value) {
        if(value !== '__new__') return
        this.modal.categoryType = 'subcategory'
        this.newCategoryName = ''
        this.modal.category = true
      },
      saveNewCategory(event) {
        if(!this.newCategoryName) {
          event.preventDefault()
          return
        }

        if(this.modal.categoryType === 'category') {
          this.form.category = this.newCategoryName
          this.form.subcategory = ''
          if(!this.options.categories.find(item => item.name === this.newCategoryName)) {
            this.options.categories.push({ id: 0, name: this.newCategoryName, subcategorys: [] })
          }
        } else {
          this.form.subcategory = this.newCategoryName
          let category = this.options.categories.find(item => item.name === this.form.category)
          if(category && !category.subcategorys.find(item => item.name === this.newCategoryName)) {
            category.subcategorys.push({ id: 0, name: this.newCategoryName })
          }
        }
      },
      resetField(field) {
        if(this.originalForm[field] === undefined) return
        this.form[field] = JSON.parse(JSON.stringify(this.originalForm[field]))
        if(field === 'barcode') {
          this.barcodeEditEnabled = false
        }
        if(this.codeEditEnabled.hasOwnProperty(field)) {
          this.codeEditEnabled[field] = false
        }
        this.recalculateSalePrices()
      },
      validateForm() {
        if(this.form.barcode) {
          this.form.code_system = this.form.barcode
          this.form.code = this.form.barcode
        }
        if(!this.form.name) return 'El nombre no puede estar vacío'
        if(!this.form.category) return 'La categoría no puede estar vacía'
        if(!this.form.subcategory) return 'La subcategoría no puede estar vacía'
        if(!this.form.code_system) {
          this.form.code_system = this.form.code || this.form.barcode
        }
        if(!this.form.code_system) return 'El código propio no puede estar vacío'
        if(!this.form.suppliers_id || !this.form.code) {
          let fallbackSupplier = this.defaultSupplierProduct || this.suppliersProducts[0] || null
          if(fallbackSupplier) {
            this.form.suppliers_id = fallbackSupplier.suppliers_id
            this.form.code = fallbackSupplier.code || this.form.barcode || this.form.code_system
          }
        }
        if(!this.form.suppliers_id) return 'No hay un proveedor default vinculado para guardar este producto'
        if(!this.form.code) return 'El código del proveedor no puede estar vacío'
        if(!this.form.unit_measurement_purchase_id) return 'La unidad de compra es obligatoria'
        return null
      },
      saveProduct() {
        this.applyDefaultSupplierToForm()
        let error = this.validateForm()
        if(error) {
          this.$awn.alert(error)
          return
        }

        if(!this.parameters.enableProductShortName) {
          this.form.name_short = this.form.name
        }

        this.confirmDialog('¿Guardar los cambios del producto?', {
          title: 'Guardar producto',
          icon: 'save',
          okTitle: 'Guardar',
        }).then(confirmed => {
          if(confirmed) {
            this.doSaveProduct()
          }
        })
      },
      doSaveProduct() {
        this.loading.save = true
        serviceAPI.saveProduct({
          ...this.form,
          sync_supplier_codes_with_barcode: !!this.form.barcode,
          skip_supplier_price_update: true,
          edit_source: 'crudProductSuppliersByBarcode',
          staff_id: Helper.getStaffId(),
        })
          .then(() => {
            this.loading.save = false
            this.$awn.success('Producto guardado')
            if(this.searchMode === 'barcode') {
              this.barcode = this.form.barcode
            }
            this.barcodeEditEnabled = false
            this.codeEditEnabled = { code: false, code_system: false }
            this.reloadCurrentProduct()
          })
          .catch(error => {
            this.loading.save = false
            this.$awn.alert(Error.showError(error))
          })
      },
      recalculateSalePrices() {
        if(!this.form.lists) return
        this.syncPriceListIva()
        this.form.lists.forEach(list => this.updateSalePrice(list))
      },
      syncPriceListIva() {
        this.form.price_list_iva = this.roundMoney(this.toNumber(this.form.price_list) * (1 + this.ivaAliquot / 100))
      },
      onPriceListIvaInput() {
        const ivaFactor = 1 + this.ivaAliquot / 100
        if (ivaFactor > 0) {
          this.form.price_list = this.roundMoney(this.toNumber(this.form.price_list_iva) / ivaFactor)
        }
        this.recalculateSalePrices()
      },
      onPriceWithIvaToggle() {
        this.syncPriceListIva()
        this.savePreferences()
      },
      onListToggle(list) {
        if(this.priceListsReadOnly) {
          this.$awn.warning('Guardá el cambio de IVA antes de editar las listas de precios')
          list.add = !list.add
          return
        }
        if(!this.form.products_id) {
          this.$awn.alert('Guardá el producto antes de modificar las listas de precios')
          list.add = !list.add
          return
        }
        if(list.add) {
          list.saving = true
          serviceAPI.agregarPrecioListaProducto({
            price_list_id: list.price_list_id,
            products_id: this.form.products_id,
            utility: list.utility,
            desc: list.desc,
            edit_source: 'crudProductSuppliersByBarcode',
            staff_id: Helper.getStaffId(),
          }).then(response => {
            list.id = response.data && response.data.id ? response.data.id : 0
            list.saving = false
            this.markListSaved(list)
            this.updateSalePrice(list)
          }).catch(error => {
            list.add = false
            list.saving = false
            this.$awn.alert(Error.showError(error))
          })
        } else {
          if(!list.id) {
            this.markListSaved(list)
            return
          }
          list.saving = true
          serviceAPI.eliminarPrecioListaProducto(list.id, { edit_source: 'crudProductSuppliersByBarcode', staff_id: Helper.getStaffId() }).then(() => {
            list.id = 0
            list.saving = false
            this.markListSaved(list)
          }).catch(error => {
            list.add = true
            list.saving = false
            this.$awn.alert(Error.showError(error))
          })
        }
      },
      prepareManualListSave(list) {
        if(!list) return
        list.manualSaveRequested = true
      },
      forceSaveListField(list) {
        if(!list) return
        list.manualSaveRequested = false
        this.saveListField(list, true)
      },
      saveListField(list, force = false) {
        if(this.priceListsReadOnly) {
          this.$awn.warning('Guardá el cambio de IVA antes de editar las listas de precios')
          return
        }
        if(list.manualSaveRequested && !force) return
        if(!list.add || !list.id || !this.form.products_id) return
        if(!this.listHasPersistedChanges(list)) return
        list.manualSaveRequested = false
        list.saved = false
        list.saving = true
        serviceAPI.editarPrecioListaProducto({
          id: list.id,
          utility: list.utility,
          desc: list.desc,
          edit_source: 'crudProductSuppliersByBarcode',
          staff_id: Helper.getStaffId(),
        }).then(() => {
          list.saving = false
          this.markListSaved(list)
        }).catch(error => {
          list.saving = false
          list.manualSaveRequested = false
          this.$awn.alert(Error.showError(error))
        })
      },
      markListSaved(list) {
        this.updateOriginalListSnapshot(list)
        list.saved = true
        setTimeout(() => {
          if(list) {
            list.saved = false
          }
        }, 2200)
      },
      updateOriginalListSnapshot(list) {
        if(!this.originalForm.lists) return
        let original = this.originalForm.lists.find(item => item.price_list_id === list.price_list_id)
        if(!original) return
        original.id = list.id
        original.add = list.add
        original.utility = list.utility
        original.desc = list.desc
        original.sale_price = list.sale_price
        original.sale_price_iva = list.sale_price_iva
      },
      listHasPersistedChanges(list) {
        if(!this.originalForm.lists) return true
        let original = this.originalForm.lists.find(item => item.price_list_id === list.price_list_id)
        if(!original) return true
        return this.valuesAreDifferent(list.utility, original.utility) ||
          this.valuesAreDifferent(list.desc, original.desc)
      },
      effectiveListUtility(list) {
        return effectiveUtility({
          utility: list ? list.utility : 0,
          defaultUtility: list ? list.default_utility : 0,
        })
      },
      refreshListUtilitySource(list) {
        if(!list) return
        list.using_default_utility = isUsingDefaultUtility({ utility: list.utility, defaultUtility: list.default_utility })
      },
      updateSalePrice(list) {
        this.refreshListUtilitySource(list)
        let { salePrice, salePriceIva } = salePriceFromCost({
          cost: this.baseCost,
          utility: this.effectiveListUtility(list),
          discount: list.desc,
          ivaAliquot: this.ivaAliquot,
        })
        list.sale_price = salePrice
        list.sale_price_iva = salePriceIva
      },
      updateUtilityFromSalePrice(list) {
        let cost = this.baseCost
        let saleIva = this.toNumber(list.sale_price_iva)
        let utility = utilityFromSalePrice({
          cost,
          salePrice: saleIva,
          discount: list.desc,
          ivaAliquot: this.ivaAliquot,
        })
        if(utility === null) {
          list.utility = 0
          this.refreshListUtilitySource(list)
          return
        }
        list.sale_price = this.roundMoney(netFromGross(saleIva, this.ivaAliquot))
        list.utility = utility
        this.refreshListUtilitySource(list)
      },
      toNumber(value) {
        let number = parseFloat(value)
        return isNaN(number) ? 0 : number
      },
      roundMoney(value) {
        return pmRoundMoney(value)
      },
      roundUtility(value) {
        return pmRoundUtility(value)
      },
      valuesAreDifferent(current, original) {
        if(this.isNumericLike(current) || this.isNumericLike(original)) {
          return this.toNumber(current) !== this.toNumber(original)
        }
        let currentComparable = current === null || current === undefined ? '' : current
        let originalComparable = original === null || original === undefined ? '' : original
        return currentComparable !== originalComparable
      },
      codesAreDifferent(current, original) {
        return this.normalizeCode(current) !== this.normalizeCode(original)
      },
      normalizeCode(value) {
        if(value === null || value === undefined) return ''
        return String(value).trim()
      },
      isNumericLike(value) {
        if(value === null || value === undefined || value === '') return false
        return !isNaN(parseFloat(value)) && isFinite(value)
      },
      money(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(this.toNumber(value))
      },
      costWithIva(value) {
        return this.roundMoney(this.toNumber(value) * (1 + this.ivaAliquot / 100))
      },
      _parseDate(value) {
        if(!value) return null
        let dateStr = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
          ? value + 'T00:00:00'
          : value
        let d = new Date(dateStr)
        return isNaN(d.getTime()) ? null : d
      },
      daysAgo(value) {
        let date = this._parseDate(value)
        if(!date) return '-'
        let today = new Date()
        today.setHours(0, 0, 0, 0)
        date.setHours(0, 0, 0, 0)
        let diff = Math.floor((today.getTime() - date.getTime()) / 86400000)
        if(diff <= 0) return 'hoy'
        if(diff === 1) return 'hace 1 día'
        return 'hace ' + diff + ' días'
      },
      dateAgoClass(value) {
        let date = this._parseDate(value)
        if(!date) return ''
        let today = new Date()
        today.setHours(0, 0, 0, 0)
        date.setHours(0, 0, 0, 0)
        let diff = Math.floor((today.getTime() - date.getTime()) / 86400000)
        if(diff <= 0) return 'meta-date--today'
        if(diff <= 7) return 'meta-date--recent'
        if(diff <= 30) return 'meta-date--normal'
        return 'meta-date--old'
      },
      openStockAdjustModal() {
        if(!this.productControlsStock) {
          this.$bvToast.toast('Este producto no controla stock y no permite ajustes por depósito', { title: 'Sin control de stock', variant: 'warning', solid: true })
          return
        }
        this.stockAdjust.deposits = this.stock.byDepositRaw.map(dep => {
          const qty = this.toNumber(dep.current_quantity)
          return Object.assign({}, dep, { real_quantity: qty })
        })
        this.modal.stockAdjust = true
      },
      stockAdjustHasChanged(dep) {
        return Math.abs(this.toNumber(dep.real_quantity) - this.toNumber(dep.current_quantity)) >= 0.00005
      },
      async processStockAdjustments() {
        if(!this.productControlsStock) {
          this.$bvToast.toast('Este producto no controla stock y no permite ajustes por depósito', { title: 'Sin control de stock', variant: 'warning', solid: true })
          return
        }
        if (this.stockAdjustChangedCount === 0) return
        const unitRef = this.selectedStockUnit && this.selectedStockUnit.reference
          ? ' en ' + this.selectedStockUnit.reference : ''
        const confirm = await this.confirmDialog(
          '¿Procesar ' + this.stockAdjustChangedCount + ' ajuste(s) de stock' + unitRef + '?',
          { title: 'Confirmar ajuste', icon: 'check2-circle', okTitle: 'Procesar' }
        )
        if (!confirm) return
        this.stockAdjust.loading = true
        const adjustments = this.stockAdjust.deposits
          .filter(d => this.stockAdjustHasChanged(d))
          .map(d => ({ deposit_id: d.deposit.id, real_quantity: d.real_quantity, current_quantity: d.current_quantity }))
        serviceAPI.procesarAjustesStock({
          product_id: this.product.id,
          unit_measurement_id: this.selectedStockUnit ? this.selectedStockUnit.id : null,
          adjustments,
          edit_source: 'crudProductSuppliersByBarcode',
        })
          .then(() => {
            this.stockAdjust.loading = false
            this.modal.stockAdjust = false
            this.stockAdjustedInSession = true
            this.loadStock()
            this.$bvToast.toast('Ajuste de stock procesado correctamente', { title: 'Stock actualizado', variant: 'success', solid: true })
          })
          .catch(error => {
            this.stockAdjust.loading = false
            this.$bvToast.toast(Error.showError(error), { title: 'Error', variant: 'danger', solid: true })
          })
      },
      formatDate(value) {
        let date = this._parseDate(value)
        if(!date) return ''
        return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      },
      loadStock() {
        if(!this.product || !this.product.id) return
        if(!this.productControlsStock) {
          this.resetStock()
          return
        }
        this.stock.loading = true
        this.stock.byDeposit = []
        this.stock.byDepositRaw = []
        serviceAPI.obtenerStockPorProducto({ product_id: this.product.id })
          .then(response => {
            this.stock.unitMeasurement = response.data.unit_measurement || null
            this.stock.unitMeasurements = response.data.unit_measurements || (response.data.unit_measurement ? [response.data.unit_measurement] : [])
            this.stock.selectedUnitId = this.stock.unitMeasurement ? this.stock.unitMeasurement.id : null
            this.stock.byDepositRaw = response.data.stock_by_deposit || []
            this.applyStockUnit()
            this.stock.loading = false
          })
          .catch(() => {
            this.stock.loading = false
          })
      },
      applyStockUnit() {
        const selected = this.selectedStockUnit
        this.stock.byDeposit = this.stock.byDepositRaw.map(dep => {
          let qty = dep.current_quantity
          if(selected && selected.type === 'sale' && dep.current_quantity_sale != null) {
            qty = dep.current_quantity_sale
          }
          if(selected && selected.type === 'purchase' && dep.current_quantity_purchase != null) {
            qty = dep.current_quantity_purchase
          }
          return Object.assign({}, dep, { current_quantity: parseFloat(qty) || 0 })
        })
      },
      resetStock() {
        this.stock.byDeposit = []
        this.stock.byDepositRaw = []
        this.stock.unitMeasurements = []
        this.stock.unitMeasurement = null
        this.stock.selectedUnitId = null
        this.stock.loading = false
      },
      formatStockQty(val) {
        const n = parseFloat(val)
        if(isNaN(n)) return '0'
        return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(n)
      },
      loadPreferences() {
        try {
          const saved = localStorage.getItem('bcsp_prefs')
          if (!saved) return
          const parsed = JSON.parse(saved)
          if (parsed && typeof parsed === 'object') {
            if (parsed.searchMode === 'manual') this.searchMode = 'manual'
            if (typeof parsed.detail === 'boolean') this.sections.detail = parsed.detail
            if (typeof parsed.suppliers === 'boolean') this.sections.suppliers = parsed.suppliers
            if (typeof parsed.priceLists === 'boolean') this.sections.priceLists = parsed.priceLists
            if (typeof parsed.stock === 'boolean') this.sections.stock = parsed.stock
            if (typeof parsed.priceWithIva === 'boolean') this.modal.priceWithIva = parsed.priceWithIva
          }
        } catch (_) { /* localStorage unavailable */ }
      },
      savePreferences() {
        try {
          const stored = localStorage.getItem('bcsp_prefs')
          const prefs = stored ? JSON.parse(stored) : {}
          prefs.searchMode = this.searchMode
          prefs.detail = this.sections.detail
          prefs.suppliers = this.sections.suppliers
          prefs.priceLists = this.sections.priceLists
          prefs.stock = this.sections.stock
          prefs.priceWithIva = this.modal.priceWithIva
          localStorage.setItem('bcsp_prefs', JSON.stringify(prefs))
        } catch (_) { /* localStorage unavailable */ }
      },
      switchSearchMode(mode) {
        this.searchMode = mode
        this.barcode = ''
        this.barcodeEditEnabled = false
        this.codeEditEnabled = { code: false, code_system: false }
        this.manualSearch.query = ''
        this.manualSearch.results = []
        this.manualSearch.show = false
        this.manualSearch.activeIdx = -1
        this.manualSearch.focusTarget = 'input'
        this.searchError = null
        this.focusSearch()
      },
      doManualSearch() {
        const q = this.manualSearch.query.trim()
        if (!q) return
        this.manualSearch.loading = true
        this.manualSearch.show = true
        this.manualSearch.activeIdx = -1
        this.manualSearch.focusTarget = 'input'
        serviceAPI.buscarProductosCostosPorTexto({ query: q })
          .then(response => {
            this.manualSearch.loading = false
            this.manualSearch.results = response.data || []
            this.manualSearch.activeIdx = -1
            this.manualSearch.focusTarget = 'input'
          })
          .catch(error => {
            this.manualSearch.loading = false
            this.manualSearch.results = []
            this.$awn.alert(Error.showError(error))
          })
      },
      manualSearchMoveDown() {
        const max = this.manualSearch.results.length
        this.manualSearch.focusTarget = 'dropdown'
        if (this.manualSearch.activeIdx < max) {
          this.manualSearch.activeIdx++
          this.$nextTick(() => this.scrollActiveDropdownItem())
        }
      },
      manualSearchMoveUp() {
        this.manualSearch.focusTarget = 'dropdown'
        if (this.manualSearch.activeIdx > -1) {
          this.manualSearch.activeIdx--
          this.$nextTick(() => this.scrollActiveDropdownItem())
        }
      },
      scrollActiveDropdownItem() {
        const container = this.$refs.manualDropdown
        if (!container) return
        const active = container.querySelector('.manual-search-item--active')
        if (active) active.scrollIntoView({ block: 'nearest' })
      },
      manualSearchConfirm() {
        if (this.manualSearch.focusTarget !== 'dropdown') {
          this.doManualSearch()
          return
        }
        if (!this.manualSearch.show || this.manualSearch.loading) {
          this.doManualSearch()
          return
        }
        const idx = this.manualSearch.activeIdx
        if (idx === -1) {
          this.doManualSearch()
          return
        }
        if (idx >= 0 && idx < this.manualSearch.results.length) {
          this.selectManualResult(this.manualSearch.results[idx])
          return
        }
        if (idx === this.manualSearch.results.length && this.manualSearch.query.trim()) {
          this.selectNewProductWithoutBarcode()
        }
      },
      manualSearchBlur() {
        this._manualBlurTimer = setTimeout(() => { this.manualSearch.show = false }, 200)
      },
      manualSearchFocus() {
        this.manualSearch.focusTarget = 'input'
        if (this.manualSearch.results.length > 0 || this.manualSearch.query.trim()) {
          this.manualSearch.show = true
        }
      },
      onDropdownItemTouchStart(e) {
        this._touchStartY = e.touches[0].clientY
        clearTimeout(this._manualBlurTimer)
      },
      onDropdownItemTouchEnd(item, e) {
        const dy = Math.abs(e.changedTouches[0].clientY - this._touchStartY)
        if (dy < 10) {
          if (item) this.selectManualResult(item)
          else this.selectNewProductWithoutBarcode()
        }
      },
      selectManualResult(product) {
        this.manualSearch.show = false
        this.loading.search = true
        this.searchError = null
        serviceAPI.cargarProductoCostosPorId({ product_id: product.id })
          .then(response => {
            this.loading.search = false
            this.applySearchResult(response.data)
          })
          .catch(error => {
            this.loading.search = false
            this.searchError = Error.showError(error)
          })
      },
      selectNewProductWithoutBarcode() {
        this.manualSearch.show = false
        const name = this.manualSearch.query.trim()
        this.applySearchResult({ exists: false, barcode: '', product: null, suppliers_products: [] })
        this.$nextTick(() => {
          this.form.name = name
        })
      },
      async startCamera() {
        this.modal.camera = true
        await this.$nextTick()
        try {
          if ('BarcodeDetector' in window) {
            this.cameraEngine = 'native'
            this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
            await this.attachCameraPreview(this.cameraStream)
            this.cameraDetector = new window.BarcodeDetector({
              formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e']
            })
            this.cameraInterval = setInterval(this.detectBarcode, 400)
          } else {
            // iOS/Safari: sin BarcodeDetector se usa Quagga, que hace su propio
            // getUserMedia e inserta su propio <video> y <canvas> DENTRO del target,
            // por eso el target debe ser el div contenedor (nunca el <video>).
            this.cameraEngine = 'quagga'
            const Quagga = (await import('@ericblade/quagga2')).default
            await new Promise((resolve, reject) => {
              Quagga.init({
                inputStream: {
                  type: 'LiveStream',
                  target: this.$refs.cameraContainer,
                  constraints: { facingMode: 'environment' },
                },
                decoder: { readers: ['ean_reader', 'ean_8_reader', 'code_128_reader', 'code_39_reader', 'upc_reader', 'upc_e_reader'] },
              }, (err) => { if (err) reject(err); else resolve() })
            })
            Quagga.start()
            this.quaggaInstance = Quagga
            this.ensureQuaggaVideoInline()
            Quagga.onDetected((data) => {
              const code = data && data.codeResult && data.codeResult.code
              if (code) {
                this.barcode = code
                this.stopCamera()
                this.modal.camera = false
                this.searchProduct()
              }
            })
          }
        } catch (e) {
          this.$bvToast.toast('No se pudo acceder a la cámara', { title: 'Error', variant: 'danger', solid: true })
          this.stopCamera()
          this.modal.camera = false
        }
      },
      // El video que inyecta Quagga necesita playsinline para que iOS no lo
      // bloquee (sin eso, Safari se niega a reproducirlo dentro de la página).
      ensureQuaggaVideoInline() {
        const container = this.$refs.cameraContainer
        if (!container) return
        const injected = Array.prototype.slice.call(container.querySelectorAll('video'))
          .find((v) => v !== this.$refs.videoElement)
        if (injected) {
          injected.setAttribute('playsinline', '')
          injected.setAttribute('webkit-playsinline', '')
          injected.setAttribute('muted', '')
          injected.muted = true
        }
      },
      async attachCameraPreview(stream) {
        const video = this.$refs.videoElement
        if (!video) return

        video.setAttribute('playsinline', '')
        video.setAttribute('webkit-playsinline', '')
        video.muted = true
        video.srcObject = stream

        if (video.readyState < 1) {
          await new Promise(resolve => {
            let resolved = false
            const done = () => {
              if (resolved) return
              resolved = true
              video.removeEventListener('loadedmetadata', done)
              resolve()
            }
            video.addEventListener('loadedmetadata', done, { once: true })
            setTimeout(done, 800)
          })
        }

        if (typeof video.play === 'function') {
          try {
            await video.play()
          } catch (e) {
            // Algunos navegadores bloquean play(); el stream sigue disponible para el detector.
          }
        }
      },
      async detectBarcode() {
        if (!this.$refs.videoElement || !this.cameraDetector) return
        if (this.$refs.videoElement.readyState < 2) return
        try {
          var barcodes = await this.cameraDetector.detect(this.$refs.videoElement)
          if (barcodes.length > 0) {
            this.barcode = barcodes[0].rawValue
            this.stopCamera()
            this.modal.camera = false
            this.searchProduct()
          }
        } catch (_e) { /* ignorar frames sin código decodificable */ }
      },
      stopCamera() {
        clearInterval(this.cameraInterval)
        this.cameraInterval = null
        if (this.quaggaInstance) {
          this.quaggaInstance.offDetected()
          this.quaggaInstance.stop()
          this.quaggaInstance = null
        }
        // Saco el video/canvas que Quagga inyectó en el contenedor, para que
        // no se acumulen al volver a abrir el modal.
        const container = this.$refs.cameraContainer
        if (container) {
          Array.prototype.slice.call(container.children).forEach((child) => {
            if (child !== this.$refs.videoElement) container.removeChild(child)
          })
        }
        if (this.cameraStream) {
          this.cameraStream.getTracks().forEach(function(t) { t.stop() })
          this.cameraStream = null
        }
        this.cameraDetector = null
        this.cameraEngine = null
      },
    }
  }
</script>

<style scoped>
  .barcode-cost-page {
    min-height: 100vh;
    background: #f8f9fa;
    font-size: 1.03rem;
  }
  .barcode-cost-header {
    z-index: 100;
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .barcode-cost-header h5 {
    font-size: 1.18rem;
  }
  .barcode-cost-header small {
    font-size: 0.84rem;
  }
  .card-section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 0.55rem;
  }
  .barcode-cost-page .card {
    border-radius: 0.7rem;
  }
  .barcode-cost-page .card-body {
    padding: 1.05rem;
  }
  .mobile-panel-toggle {
    display: none;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 1.8rem;
    border-radius: 999px;
    font-weight: 700;
    line-height: 1;
    padding: 0 0.55rem;
  }
  .search-card {
    width: 100%;
    margin: 0;
  }
  .barcode-cost-page .btn-lg {
    padding: 0.8rem 1.15rem;
    font-size: 1.02rem;
  }
  .barcode-cost-page .btn-sm {
    padding: 0.42rem 0.8rem;
    font-size: 0.9rem;
  }
  .barcode-cost-page .form-control,
  .barcode-cost-page .custom-select,
  .barcode-cost-page .input-group-text {
    min-height: 2.8rem;
    font-size: 1rem;
  }
  .barcode-cost-page .form-control-lg {
    min-height: 3.35rem;
    font-size: 1.02rem;
  }
  .barcode-cost-page .input-group > .input-group-prepend > .input-group-text,
  .barcode-cost-page .input-group > .input-group-append > .input-group-text,
  .barcode-cost-page .input-group > .input-group-append > .btn,
  .barcode-cost-page .input-group > .input-group-prepend > .btn {
    min-height: 2.8rem;
  }
  .section-title {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    color: #495057;
    font-size: 0.8rem;
    font-weight: 800;
    letter-spacing: 0.05rem;
    margin-bottom: 0.75rem;
    text-transform: uppercase;
  }
  .section-title__icon {
    color: #0d6efd;
    font-size: 0.95rem;
  }
  .section-title__count {
    border: 1px solid #dbe4f0;
    color: #0f5f6d;
    font-size: 0.72rem;
    letter-spacing: 0;
    padding: 0.2rem 0.5rem;
    text-transform: none;
  }
  .product-progress {
    border: 1px solid;
    border-radius: 8px;
    overflow: hidden;
  }
  .product-progress--danger {
    background: #fff0eb;
    border-color: #e6a69a;
    color: #8f2e22;
  }
  .product-progress--warning {
    background: #fff4db;
    border-color: #e9bd63;
    color: #744b06;
  }
  .product-progress--complete {
    background: #e5f5e9;
    border-color: #9bd2aa;
    color: #176243;
  }
  .product-progress__toggle {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    width: 100%;
    padding: 0.55rem 0.75rem;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    text-align: left;
  }
  .product-progress__toggle:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -3px;
  }
  .product-progress__indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.65);
  }
  .product-progress__message {
    display: flex;
    align-items: baseline;
    flex: 1;
    flex-wrap: wrap;
    gap: 0.15rem 0.5rem;
    min-width: 0;
    font-size: 0.88rem;
  }
  .product-progress__message strong {
    white-space: nowrap;
  }
  .product-progress__next {
    position: relative;
    display: inline-block;
    overflow: hidden;
    border-radius: 4px;
    padding: 0.12rem 0.3rem;
    margin: -0.12rem -0.3rem;
    animation: product-progress-attention 2.4s ease-out;
  }
  .product-progress__next::after {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -45%;
    width: 35%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.9), transparent);
    content: '';
    transform: skewX(-20deg);
    animation: product-progress-shine 1.1s ease-out 0.2s both;
    pointer-events: none;
  }
  .product-progress__count {
    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .product-progress__chevron {
    flex: 0 0 auto;
    font-size: 0.85rem;
  }
  .product-progress__details {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    border-top: 1px solid rgba(80, 80, 80, 0.15);
    padding: 0.6rem 0.85rem 0.7rem;
    font-size: 0.86rem;
  }
  .product-progress__step {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    opacity: 0.75;
  }
  .product-progress__step--done {
    opacity: 1;
  }
  .product-progress__step-icon {
    flex: 0 0 1rem;
    text-align: center;
  }
  .product-progress__hint {
    margin-top: 0.3rem;
  }
  @keyframes product-progress-attention {
    0% { transform: translateY(5px); background: rgba(255, 255, 255, 0.95); box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.55); }
    15% { transform: translateY(0); }
    65% { background: rgba(255, 255, 255, 0.95); box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.55); }
    100% { background: transparent; box-shadow: none; }
  }
  @keyframes product-progress-shine {
    0% { left: -45%; }
    100% { left: 120%; }
  }
  @media (prefers-reduced-motion: reduce) {
    .product-progress__next,
    .product-progress__next::after { animation: none; }
  }
  .product-header-card {
    border-left-width: 4px !important;
  }
  .product-header-badge {
    font-size: 0.72rem;
    padding: 0.25em 0.7em;
  }
  .product-header-barcode {
    font-size: 0.85rem;
    color: #6c757d;
    letter-spacing: 0.03em;
  }
  .product-header-name {
    font-size: 1.2rem;
    color: #1a202c;
    line-height: 1.3;
  }
  .product-header-reset {
    font-size: 0.8rem;
    color: #6c757d;
    border-color: #dee2e6;
  }
  .product-sections-toggle {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem 0.75rem;
    justify-content: center;
  }
  .product-sections-toggle small {
    font-size: 0.78rem;
    color: #495057;
  }
  .product-identity-bar {
    display: flex;
    align-items: center;
    background: #f0f4f8;
    border: 1px solid #d0dce8;
    border-radius: 6px;
    padding: 0.45rem 0.75rem;
  }
  .product-identity-code {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-weight: 600;
    font-size: 1rem;
    letter-spacing: 0.04em;
    color: #2d3748;
  }
  .barcode-edit-readonly {
    background-color: #f8f9fa !important;
    color: #6c757d;
    cursor: default;
  }
  .section-divider {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #adb5bd;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .section-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e9ecef;
  }
  .supplier-list {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }
  .supplier-item {
    padding: 0.72rem 0.95rem;
    cursor: pointer;
    border-bottom: 1px solid #f0f2f5;
    background: #fff;
    transition: background-color 0.1s;
  }
  .supplier-item:last-child {
    border-bottom: none;
  }
  .supplier-item:hover {
    background: #f8f9fa;
  }
  .supplier-item--default {
    background: #fefce8;
  }
  .supplier-item--selected {
    background: #f3e8ff !important;
    box-shadow: inset 4px 0 0 #7c3aed;
  }
  .supplier-item__body {
    flex-grow: 1;
    min-width: 0;
  }
  .supplier-item__name {
    font-weight: 600;
    font-size: 0.95rem;
    color: #2d3748;
  }
  .supplier-item__badge {
    font-size: 0.65rem;
  }
  .supplier-item__details {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.2rem;
    font-size: 0.8rem;
    color: #6c757d;
  }
  .supplier-item__code {
    background: #edf2f7;
    border-radius: 3px;
    padding: 0 0.3rem;
    font-family: monospace;
    color: #4a5568;
  }
  .supplier-item__cost {
    font-weight: 700;
    color: #0369a1;
    font-size: 0.88rem;
  }
  .supplier-item__cost--iva-primary {
    font-weight: 800;
    color: #0369a1;
    font-size: 0.95rem;
  }
  .supplier-item__cost--iva-secondary {
    font-weight: 500;
    color: #94a3b8;
    font-size: 0.78rem;
  }
  .base-cost-bar__label--iva {
    color: #1d4ed8;
  }
  .base-cost-bar__final--iva {
    color: #1d4ed8;
    font-size: 1.18rem;
  }
  .cost-steps-iva {
    color: #1d4ed8;
    border-top: 1.5px solid #bfdbfe !important;
    margin-top: 4px;
    padding-top: 4px;
  }
  .supplier-item__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-top: 0.3rem;
    font-size: 0.78rem;
    color: #6c757d;
  }
  .supplier-item__meta-item {
    display: flex;
    align-items: center;
  }
  .supplier-item__meta-item--empty {
    color: #adb5bd;
    font-style: italic;
  }
  .supplier-item__meta-label {
    font-weight: 600;
    margin-right: 0.2rem;
    font-style: normal;
  }
  .meta-date--today {
    color: #16a34a;
    font-weight: 700;
  }
  .meta-date--recent {
    color: #0284c7;
    font-weight: 600;
  }
  .meta-date--normal {
    color: #4a5568;
  }
  .meta-date--old {
    color: #9ca3af;
  }
  .supplier-item__action-bar {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding-top: 0.4rem;
    border-top: 1px solid #e9ecef;
  }
  .add-supplier-box {
    border-top: 1px solid #e9ecef;
    padding-top: 0.75rem;
  }
  .supplier-add-label {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 0.82rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.3rem;
  }
  .supplier-add-group {
    align-items: stretch;
  }
  .supplier-add-select {
    min-width: 0;
    flex: 1 1 auto;
  }
  .supplier-add-button {
    min-width: 44px;
    padding-left: 0;
    padding-right: 0;
  }
  .unit-slot {
    margin-bottom: 0.75rem;
  }
  .unit-slot__label {
    font-size: 0.82rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.3rem;
  }
  .unit-conversion-label {
    display: block;
    font-size: 0.82rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.3rem;
  }
  .supplier-list__hint {
    font-size: 0.76rem;
    letter-spacing: 0.01em;
  }
  .supplier-modal-code-row {
    display: flex;
    align-items: center;
  }
  .supplier-modal-code {
    font-size: 0.9rem;
    color: #4a5568;
    background: #edf2f7;
    padding: 0.1rem 0.45rem;
    border-radius: 3px;
  }
  .modal-breakdown-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    color: #4a5568;
    cursor: pointer;
    margin-top: 0.35rem;
    padding: 0.58rem 0.75rem;
    text-align: left;
  }
  .modal-breakdown-toggle:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
  .modal-breakdown-toggle__label {
    display: flex;
    align-items: center;
    min-width: 0;
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    font-weight: 600;
  }
  .modal-breakdown-toggle__status {
    margin-left: 0.45rem;
    border-radius: 999px;
    background: #fff7ed;
    color: #9a3412;
    border: 1px solid #fed7aa;
    padding: 0.08rem 0.42rem;
    font-size: 0.64rem;
    text-transform: none;
    letter-spacing: 0;
    white-space: nowrap;
  }
  .modal-breakdown-toggle__icon {
    color: #64748b;
    flex-shrink: 0;
    margin-left: 0.5rem;
  }
  .modal-section-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #adb5bd;
    margin-bottom: 0.4rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .modal-section-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e9ecef;
  }
  .cost-steps-box {
    border: 1px solid #fcd34d;
    background: #fffbeb;
    border-radius: 6px;
    padding: 0.55rem 0.85rem;
  }
  .cost-steps-row {
    display: grid;
    grid-template-columns: 1fr 1rem auto;
    gap: 0.4rem;
    align-items: center;
    padding: 0.18rem 0;
    border-bottom: 1px solid #fef3c7;
  }
  .cost-steps-row:last-child {
    border-bottom: none;
  }
  .cost-steps-final {
    padding-top: 0.3rem;
    border-top: 1.5px solid #fcd34d !important;
    border-bottom: none !important;
  }
  .cost-steps-label {
    font-size: 0.82rem;
  }
  .cost-steps-sign {
    font-weight: 700;
    font-size: 0.85rem;
    text-align: center;
  }
  .cost-steps-value {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 0.82rem;
    white-space: nowrap;
    text-align: right;
  }
  .base-cost-bar {
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 6px;
    padding: 0.7rem 0.95rem;
  }
  .base-cost-bar__hint-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 0.35rem;
  }
  .base-cost-bar__hint {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    background: #fff7ed;
    color: #9a3412;
    border: 1px solid #fed7aa;
    padding: 0.12rem 0.5rem;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: help;
  }
  .base-cost-bar__label {
    font-weight: 700;
    font-size: 0.84rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #92400e;
  }
  .base-cost-bar__final {
    font-weight: 800;
    font-size: 1.08rem;
    color: #78350f;
    margin-left: 0.25rem;
  }
  .price-list-block {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    margin-bottom: 0.75rem;
    padding: 0.85rem 1.05rem;
  }
  .price-list-block--active {
    background: #f0fdf4;
    border-color: #bbf7d0;
  }
  .price-list-block--inactive {
    background: #fef2f2;
    border-color: #fecaca;
  }
  .price-list-block--readonly {
    background: #fff7ed;
    border-color: #fed7aa;
  }
  .price-list-block__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .price-list-block__toggle {
    flex-grow: 1;
  }
  .price-list-block__name {
    font-weight: 600;
    font-size: 0.96rem;
  }
  .price-list-block__result {
    font-weight: 700;
    font-size: 1.04rem;
    margin-left: 0.5rem;
    flex-shrink: 0;
  }
  .price-list-block__result--active {
    color: #0369a1;
  }
  .price-list-save-status {
    display: inline-flex;
    align-items: center;
    margin-top: 0.45rem;
    padding: 0.18rem 0.45rem;
    border-radius: 999px;
    background: #f1f5f9;
    color: #64748b;
    font-size: 0.76rem;
    font-weight: 600;
  }
  .price-list-save-status--saved {
    background: #dcfce7;
    color: #166534;
  }
  .price-list-calc-toggle {
    margin-top: 0.35rem;
    padding: 0;
    font-size: 0.78rem;
    font-weight: 600;
  }
  .plc-box {
    margin-top: 0.4rem;
    margin-bottom: 0.25rem;
    background: #f8fafc;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 0.45rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .plc-row {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    color: #6c757d;
    gap: 0.4rem;
  }
  .plc-row--add {
    color: #1a7f37;
    font-weight: 500;
  }
  .plc-row--sub {
    color: #b91c1c;
    font-weight: 500;
  }
  .plc-row--siva {
    border-top: 1px solid #dee2e6;
    margin-top: 0.15rem;
    padding-top: 0.15rem;
    color: #374151;
  }
  .plc-row--iva {
    color: #1d4ed8;
    font-weight: 500;
  }
  .plc-hint {
    font-size: 0.7rem;
    color: #9ca3af;
    font-style: italic;
    font-weight: 400;
  }
  .price-list-sale-row {
    background: #eff6ff;
    border: 1.5px solid #93c5fd;
    border-radius: 8px;
    padding: 0.75rem 0.9rem 0.65rem;
    margin-top: 0.6rem;
  }
  .price-list-sale-row__label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #1d4ed8;
    margin-bottom: 0.4rem;
  }
  .price-list-sale-row__input {
    font-size: 1.2rem;
    font-weight: 700;
    color: #1e40af;
  }
  .price-list-save-btn {
    min-width: 2.1rem;
    padding-left: 0.45rem;
    padding-right: 0.45rem;
    line-height: 1;
  }
  .plc-label {
    white-space: nowrap;
  }
  .plc-bar {
    flex: 1;
    height: 1px;
    background: #dee2e6;
    margin: 0 0.25rem;
  }
  .plc-val {
    white-space: nowrap;
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 0.75rem;
  }
  .field-reset-restored {
    transition: background-color 0.2s ease, color 0.2s ease;
  }
  .field-group-label {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .card-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.88);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: 0.25rem;
    backdrop-filter: blur(1px);
    pointer-events: all;
  }
  .barcode-cost-page >>> .smart-select-native {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
    height: 40px;
    font-size: 1rem;
    border-radius: 0.25rem;
    appearance: auto;
  }
  .barcode-cost-page >>> .input-group > .smart-select-native {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    flex: 1 1 auto;
    width: 1%;
  }
  .barcode-cost-page >>> .supplier-add-select.smart-select-native {
    height: 39px;
  }
  .barcode-cost-page >>> .v-select {
    background: #fff;
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
  }
  .barcode-cost-page >>> .input-group > .v-select {
    position: relative;
    width: 1%;
  }
  .barcode-cost-page >>> .input-group > .v-select .vs__dropdown-toggle {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }
  .barcode-cost-page >>> .unit-slot .vs__dropdown-toggle,
  .barcode-cost-page >>> .supplier-add-select .vs__dropdown-toggle {
    min-height: 39px;
    height: 39px;
    padding-top: 0;
    padding-bottom: 0;
    align-items: center;
  }
  .barcode-cost-page >>> .unit-slot .vs__selected-options,
  .barcode-cost-page >>> .supplier-add-select .vs__selected-options {
    align-items: center;
    min-height: 100%;
  }
  .barcode-cost-page >>> .unit-slot .vs__actions,
  .barcode-cost-page >>> .supplier-add-select .vs__actions {
    height: 100%;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }
  .barcode-cost-page >>> .unit-slot .input-group-append .btn,
  .barcode-cost-page >>> .add-supplier-box .input-group-append .btn {
    height: 39px;
    min-height: 39px;
    padding-top: 0;
    padding-bottom: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .barcode-cost-page >>> .supplier-add-group,
  .barcode-cost-page >>> .supplier-add-group .v-select,
  .barcode-cost-page >>> .supplier-add-group .dropdown-toggle,
  .barcode-cost-page >>> .supplier-add-group .vs__dropdown-toggle,
  .barcode-cost-page >>> .supplier-add-group .input-group-append,
  .barcode-cost-page >>> .supplier-add-group .input-group-append .btn {
    height: 39px;
    min-height: 39px;
  }
  .barcode-cost-page >>> .supplier-add-group .dropdown-toggle,
  .barcode-cost-page >>> .supplier-add-group .vs__dropdown-toggle {
    align-items: center;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    padding-bottom: 0;
    padding-top: 0;
  }
  .barcode-cost-page >>> .supplier-add-group .input-group-append .btn {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }
  .barcode-cost-page >>> .supplier-add-group .vs__actions {
    align-items: center;
    align-self: stretch;
    display: flex;
    height: 39px;
    padding: 0 6px 0 3px;
  }
  .barcode-cost-page >>> .supplier-add-group .vs__open-indicator,
  .barcode-cost-page >>> .supplier-add-group .open-indicator {
    align-self: center;
    margin-top: 0;
  }
  .barcode-cost-page >>> .input-group > .input-group-append {
    display: flex;
  }
  .barcode-cost-page >>> .vs__dropdown-toggle {
    min-height: 39px;
  }
  .camera-preview {
    background: #000;
    border-radius: 8px;
    max-height: 60vh;
    width: 100%;
  }
  .camera-preview-container {
    position: relative;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
  }
  /* Video y canvas que inyecta Quagga (fallback en iOS/Safari) */
  .camera-preview-container >>> video {
    display: block;
    width: 100%;
    max-height: 60vh;
    object-fit: contain;
  }
  .camera-preview-container >>> canvas.drawingBuffer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .search-mode-btn {
    min-width: 3.2rem;
    font-size: 1.1rem;
  }
  .search-mode-hint {
    display: block;
    margin-top: 0.4rem;
    font-size: 0.8rem;
    line-height: 1.5;
  }
  .manual-search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #dee2e6;
    border-top: none;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.13);
    z-index: 200;
    max-height: 320px;
    overflow-y: auto;
  }
  .manual-search-empty {
    padding: 0.75rem 1rem;
    font-size: 0.88rem;
  }
  .manual-search-item {
    display: flex;
    align-items: center;
    padding: 0.65rem 1rem;
    cursor: pointer;
    border-bottom: 1px solid #f0f2f5;
    gap: 0.5rem;
    transition: background-color 0.1s;
  }
  .manual-search-item:last-child {
    border-bottom: none;
  }
  .manual-search-item:hover,
  .manual-search-item--active {
    background: #f0f4ff;
  }
  .manual-search-item--new {
    border-top: 1px dashed #a7f3d0;
    background: #f0fdf4;
  }
  .manual-search-item--new:hover,
  .manual-search-item--new.manual-search-item--active {
    background: #dcfce7;
  }
  .manual-search-item__body {
    flex-grow: 1;
    min-width: 0;
  }
  .manual-search-item__name {
    font-weight: 600;
    font-size: 0.94rem;
    color: #2d3748;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .manual-search-item__meta {
    font-size: 0.78rem;
    color: #6c757d;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.1rem;
  }
  .manual-search-item__code {
    background: #edf2f7;
    border-radius: 3px;
    padding: 0 0.25rem;
    color: #4a5568;
    font-size: 0.76rem;
  }
  .manual-search-item__barcode {
    background: #fef3c7;
    border-radius: 3px;
    padding: 0 0.25rem;
    color: #92400e;
    font-size: 0.76rem;
  }

  @media (max-width: 767.98px) {
    .barcode-cost-body {
      padding: 0.5rem !important;
    }
    .barcode-cost-body > .card,
    .barcode-cost-body .card {
      border-radius: 0.35rem;
    }
    .barcode-cost-body >>> .card-body {
      padding: 0.65rem;
    }
    .barcode-cost-body >>> .row {
      margin-left: -0.25rem;
      margin-right: -0.25rem;
    }
    .barcode-cost-body >>> [class*="col-"] {
      padding-left: 0.25rem;
      padding-right: 0.25rem;
    }
    .product-header-card .d-flex {
      align-items: flex-start !important;
      gap: 0.5rem;
    }
    .product-header-reset {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      white-space: nowrap;
    }
    .product-header-name {
      font-size: 1rem;
      word-break: break-word;
    }
    .product-identity-bar {
      padding: 0.4rem 0.5rem;
    }
    .product-identity-code {
      font-size: 0.82rem;
      min-width: 0;
      overflow-wrap: anywhere;
    }
    .card-section-heading {
      margin-bottom: 0.55rem !important;
      padding-bottom: 0.45rem;
    }
    .section-title {
      font-size: 0.76rem;
      gap: 0.35rem;
      min-width: 0;
    }
    .mobile-panel-toggle {
      display: inline-flex;
      flex-shrink: 0;
    }
    .mobile-collapsible-card {
      margin-bottom: 0.5rem !important;
    }
    .supplier-item {
      padding: 0.55rem 0.6rem;
    }
    .supplier-item__action-bar {
      flex-direction: column;
      gap: 0.35rem;
    }
    .supplier-item__action-bar .btn {
      width: 100%;
    }
    .price-list-block {
      padding: 0.6rem;
    }
    .price-list-block__header {
      align-items: flex-start;
      gap: 0.4rem;
    }
    .price-list-block__result {
      font-size: 0.88rem;
      margin-left: 0;
    }
    .price-list-sale-row__input {
      font-size: 1.05rem;
    }
    .price-list-sale-row__label small {
      display: block;
      margin-left: 0 !important;
      margin-top: 0.15rem;
    }
    .barcode-cost-page >>> .unit-slot .vs__dropdown-toggle,
    .barcode-cost-page >>> .supplier-add-select .vs__dropdown-toggle {
      min-height: 39px;
      height: 39px;
    }
    .price-list-save-btn {
      min-width: 1.9rem;
    }
    .plc-row {
      align-items: flex-start;
    }
    .plc-label {
      min-width: 0;
      white-space: normal;
    }
    .plc-bar {
      display: none;
    }
  }
  .stock-total-hero {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
    border-radius: 10px;
    padding: 1.1rem 1.3rem;
  }
  .stock-total-hero__number {
    font-size: 2.4rem;
    font-weight: 800;
    color: #fff;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
  }
  .stock-total-hero__unit {
    font-size: 1rem;
    font-weight: 600;
    color: #93c5fd;
    letter-spacing: 0.04em;
  }
  .stock-unit-toggle {
    display: flex;
    gap: 0.5rem;
  }
  .stock-unit-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    line-height: 1.2;
  }
  .stock-unit-btn:hover {
    border-color: #93c5fd;
    background: #eff6ff;
  }
  .stock-unit-btn--active {
    border-color: #1e3a5f;
    background: #1e3a5f;
  }
  .stock-unit-btn--active:hover {
    border-color: #1e3a5f;
    background: #1e3a5f;
  }
  .stock-unit-btn__ref {
    font-weight: 700;
    font-size: 1rem;
    color: #1e3a5f;
  }
  .stock-unit-btn--active .stock-unit-btn__ref {
    color: #fff;
  }
  .stock-unit-btn__type {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    margin-top: 0.1rem;
  }
  .stock-unit-btn--active .stock-unit-btn__type {
    color: #93c5fd;
  }
  .stock-deposits-list {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }
  .stock-deposit-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.55rem 0.85rem;
    border-bottom: 1px solid #f0f4f8;
    font-size: 0.88rem;
  }
  .stock-deposit-row:last-child {
    border-bottom: none;
  }
  .stock-deposit-name {
    color: #4a5568;
    display: flex;
    align-items: center;
  }
  .stock-deposit-qty {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #1e3a5f;
    font-size: 0.95rem;
  }
  .stock-deposit-qty--zero {
    color: #adb5bd;
    font-weight: 400;
  }
  .supplier-oc-btn {
    padding: 0.15rem 0.45rem;
    line-height: 1;
    font-size: 0.8rem;
    border-radius: 6px;
    opacity: 0.7;
    transition: opacity 0.15s;
  }
  .supplier-oc-btn:hover {
    opacity: 1;
  }
  .qao-product-row,
  .qao-info-row {
    display: flex;
    align-items: flex-start;
  }
  .qao-order-badge {
    display: inline-flex;
    align-items: center;
    background: #e8f4fd;
    border: 1px solid #bee3f8;
    color: #2b6cb0;
    border-radius: 6px;
    padding: 0.3rem 0.65rem;
    font-size: 0.85rem;
    font-weight: 600;
  }
  .qao-existing-item {
    font-size: 0.875rem;
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;
  }
  .stock-adjust-unit-context {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--app-color-primary-200);
    border-radius: 10px;
    background: var(--app-color-primary-soft);
  }
  .stock-adjust-unit-context__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 9px;
    background: var(--app-color-surface);
    color: var(--app-color-primary);
    font-size: 1.25rem;
    flex-shrink: 0;
  }
  .stock-adjust-unit-context__label {
    color: var(--app-color-text-secondary);
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .stock-adjust-unit-context__value {
    margin-top: 2px;
    color: var(--app-color-text);
    font-size: 1.08rem;
    font-weight: 700;
  }
  .stock-adjust-unit-context__value span {
    color: var(--app-color-text-secondary);
    font-weight: 600;
  }
  .barcode-cost-header { background: var(--app-color-primary-900); z-index: 100; }
  .barcode-cost-header__subtitle { color: var(--app-color-on-primary); opacity: 0.72; }
  .barcode-cost-page { min-height: 100vh; background: var(--app-color-canvas); }
  .barcode-cost-footer { left: 0; right: 0; z-index: 1000; background: var(--app-color-surface); }
  @media (min-width: 992px) {
    .sidebar-lg-show .barcode-cost-footer { left: 200px; width: calc(100% - 200px); }
    .sidebar-lg-show.sidebar-minimized .barcode-cost-footer { left: 50px; width: calc(100% - 50px); }
  }
</style>

<style>
.supplier-price-detail-tooltip .tooltip-inner {
  max-width: 320px;
  background: #111827;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.22);
}

.supplier-price-detail-tooltip .arrow::before {
  border-top-color: #111827;
}

.price-detail-tooltip {
  min-width: 240px;
  color: #f8fafc;
  text-align: left;
}

.price-detail-tooltip__title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fde68a;
  margin-bottom: 0.45rem;
}

.price-detail-tooltip__row,
.price-detail-tooltip__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.78rem;
  padding: 0.16rem 0;
}

.price-detail-tooltip__row span,
.price-detail-tooltip__total span {
  color: #cbd5e1;
}

.price-detail-tooltip__total {
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  margin-top: 0.28rem;
  padding-top: 0.4rem;
  font-weight: 700;
}

.price-detail-tooltip__total--iva {
  border-top: 0;
  margin-top: 0;
  color: #93c5fd;
}

body.barcode-cost-focus-mode .app-header,
body.barcode-cost-focus-mode .sidebar,
body.barcode-cost-focus-mode .aside-menu,
body.barcode-cost-focus-mode .app-footer,
body.barcode-cost-focus-mode .alert-clear-cache,
body.barcode-cost-focus-mode .shortcuts-dropdown-full {
  display: none !important;
}

body.barcode-cost-focus-mode .app-body {
  margin-top: 0 !important;
}

body.barcode-cost-focus-mode .main {
  margin-left: 0 !important;
  width: 100% !important;
}

body.barcode-cost-focus-mode .main .container-fluid {
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
}
</style>
